import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, Button, message, Select, notification, Spin } from "antd";
import {
  DeleteOutlined,
  SaveOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";

import { PDFDocument, rgb } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { v4 as uuidv4 } from "uuid";
import "pdfjs-dist/web/pdf_viewer.css";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useBlocker } from "react-router-dom";
import {
  searchDrawings,
  updateDrawing,
  detectSheetLinks,
  fetchLatestDrawingRevision,
} from "../services/drawingsAPI";
import { useAuth } from "../hooks/useAuth";
import { fetchRFIsSimple, linkRfiToDrawing, unlinkRfiFromDrawing } from "../services/rfisAPI";
import { fetchSubmittalsSimple } from "../services/submittalsAPI";
import {
  fetchPunchLists,
  fetchPunchListItems,
  linkPunchListItemToDrawing,
  unlinkPunchListItemFromDrawing,
} from "../services/punchListsAPI";
import { syncPunchListDrawingLinksOnSave } from "../utils/punchListDrawingLinks";
import { syncRfiDrawingLinksOnSave } from "../utils/rfiDrawingLinks";
import ViewRFIModal from "./ViewRFIModal";
import ViewSubmittalModal from "./ViewSubmittalModal";
import ViewPunchListItemModal from "./ViewPunchListItemModal";
import CreateRFIModal from "./CreateRFIModal";
import CreateSubmittalModal from "./CreateSubmittalModal";
import CreatePunchListItemModal from "./CreatePunchListItem";
import {
  TOOLS,
  SHAPES,
  CLOUD_CLOSE_DISTANCE,
  RFI_TAG_HEIGHT,
  CONNECTION_INNER_RECT_INSET,
  TEXT_ANNOTATION_DEFAULT_WIDTH,
} from "./drawing/constants";
import {
  getCloudPath,
  distance,
  findConnectedCloudAnnotationIndices,
  getTextAnnotationMetrics,
  mergeCloudGroupAnnotations,
  resolveTextAnnotationBoxWidth,
  getCalloutBox,
  getCalloutBoxCenter,
  getCalloutKneePoint,
  getCalloutLeaderGeometry,
  resizeCalloutBox,
  isDrawingLink,
  isAutoGeneratedDrawingLink,
  getSheetLinkHitRadius,
  mergeDetectedSheetLinks,
  applyResolvedSheetLinkTargets,
} from "./drawing/utils";
import {
  annotationHasLinkedPhoto,
  buildPhotoLinkFields,
  getLinkedPhotoDisplayUrl,
  normalizeConnectionPhotosForPersist,
} from "./drawing/utils/linkedPhoto";
import {
  downloadPdfWithAnnotations,
  downloadPdfWithoutAnnotations,
} from "./drawing/utils/pdfDownload";
import DrawingToolbar from "./drawing/components/DrawingToolbar";
import DrawingFooter from "./drawing/components/DrawingFooter";
import PageThumbnailSidebar, {
  PAGE_THUMBNAIL_SIDEBAR_WIDTH,
  PAGE_THUMBNAIL_COLLAPSED_WIDTH,
} from "./drawing/components/PageThumbnailSidebar";
import ConnectionBox from "./drawing/components/ConnectionBox";
import SheetLinkPopover from "./drawing/components/SheetLinkPopover";
import TextAnnotationInput from "./drawing/components/TextAnnotationInput";
import CalloutLeader from "./drawing/components/CalloutLeader";
import CurrentShapeRenderer from "./drawing/components/CurrentShapeRenderer";
import AnnotationRenderer from "./drawing/components/AnnotationRenderer";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/5.2.133/pdf.worker.min.js`;

const annotationIdsFrom = (items) =>
  (items || [])
    .map((item) => item?.id)
    .filter((id) => typeof id === "string" && id.length > 0);

const DrawingPreviewShell = ({
  embedded,
  title,
  visible,
  onCancel,
  width,
  footer,
  children,
  hideEmbeddedTopBar,
}) => {
  if (embedded) {
    if (!visible) return null;

    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          overflow: "hidden",
          touchAction: "none",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
        }}
      >
        {!hideEmbeddedTopBar && (
          <div
            style={{
              alignItems: "center",
              borderBottom: "1px solid #f0f0f0",
              display: "flex",
              gap: 16,
              justifyContent: "space-between",
              padding: "16px 24px",
              flexShrink: 0,
            }}
          >
            <h2 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>
              {title}
            </h2>
            <Button onClick={onCancel}>Close</Button>
          </div>
        )}
        <div
          style={{
            position: "relative",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </div>
        <div
          style={{
            borderTop: "1px solid #f0f0f0",
            padding: "10px 16px",
            flexShrink: 0,
          }}
        >
          {footer}
        </div>
      </div>
    );
  }

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onCancel}
      width={width}
      keyboard={false}
      draggable
      style={{ touchAction: "none" }}
      styles={{ mask: { touchAction: "none" } }}
      footer={footer}
    >
      {children}
    </Modal>
  );
};

const DrawingPreview = ({
  drawing,
  visible,
  onClose,
  embedded = false,
  hideEmbeddedTopBar = false,
  onNavigatePreviousDrawing,
  onNavigateNextDrawing,
  /** Folder drawings in list order — shown as stacked sheet thumbnails. */
  folderDrawings = [],
  onSelectFolderDrawing,
  /** 0-based page to open after the PDF loads (e.g. from sidebar click). */
  initialPageIndex = 0,
}) => {
  const drawingId = drawing?.id ?? drawing?._id ?? null;

  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [pageThumbsOpen, setPageThumbsOpen] = useState(true);
  const [pageViewport, setPageViewport] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [color, setColor] = useState("red");
  const [strokeWidth, setStrokeWidth] = useState(2);
  const [selectedAnnotationIndex, setSelectedAnnotationIndex] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentTool, setCurrentTool] = useState(TOOLS.PAN);
  const [currentShape, setCurrentShape] = useState(SHAPES.LINE);
  const [textInput, setTextInput] = useState("");
  const [startPoint, setStartPoint] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [textInputPos, setTextInputPos] = useState(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [calloutTip, setCalloutTip] = useState(null);
  const calloutTipRef = useRef(null);
  const [calloutKnee, setCalloutKnee] = useState(null);
  const calloutKneeRef = useRef(null);
  const [calloutDragEnd, setCalloutDragEnd] = useState(null);
  const draggingCalloutPartRef = useRef(null);
  const calloutResizeStartRef = useRef(null);
  const inputRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(TEXT_ANNOTATION_DEFAULT_WIDTH);
  const [inputHeight, setInputHeight] = useState(24);
  const manualTextHeightRef = useRef(null);
  const isResizingTextRef = useRef(false);
  /** Ignore textarea blur-commit while a mousedown is opening the editor (focus steal). */
  const suppressTextBlurRef = useRef(false);
  const showTextInputRef = useRef(false);
  const [textBold, setTextBold] = useState(false);
  const [textItalic, setTextItalic] = useState(false);
  const [textUnderline, setTextUnderline] = useState(false);
  const [textStrike, setTextStrike] = useState(false);
  const [fontSize, setFontSize] = useState(16);
  const [textHighlight, setTextHighlight] = useState(false);
  const [textSquiggly, setTextSquiggly] = useState(false);
  const [showConnectionBox, setShowConnectionBox] = useState(false);
  const [connectionBoxStart, setConnectionBoxStart] = useState(null);
  const [connectionBoxEnd, setConnectionBoxEnd] = useState(null);
  const [connectionText, setConnectionText] = useState("");
  const [isResizingConnection, setIsResizingConnection] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [isMovingConnection, setIsMovingConnection] = useState(false);
  const [connectionMoveOffset, setConnectionMoveOffset] = useState({
    x: 0,
    y: 0,
  });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkType, setLinkType] = useState(null); // 'rfi' | 'submittal' | 'punchlist' | 'drawing'
  const [selectedRfiId, setSelectedRfiId] = useState(null);
  const [drawingSearch, setDrawingSearch] = useState("");
  const [isLinkSelectOpen, setIsLinkSelectOpen] = useState(false);
  /** Punch list (folder) chosen in the link modal before picking an item */
  const [linkModalPunchListId, setLinkModalPunchListId] = useState(null);
  const [showPunchListItemModal, setShowPunchListItemModal] = useState(false);
  const [viewPunchListId, setViewPunchListId] = useState(null);
  const [viewPunchListItemId, setViewPunchListItemId] = useState(null);
  const [rfiTags, setRfiTags] = useState([]);
  const [draggingRfiTag, setDraggingRfiTag] = useState(null);
  const [showRfiModal, setShowRfiModal] = useState(false);
  const [showSubmittalModal, setShowSubmittalModal] = useState(false);
  const [showCreateRfiModal, setShowCreateRfiModal] = useState(false);
  const [showCreateSubmittalModal, setShowCreateSubmittalModal] =
    useState(false);
  const [isPunchListFolderSelectOpen, setIsPunchListFolderSelectOpen] =
    useState(false);
  const [isPunchListItemSelectOpen, setIsPunchListItemSelectOpen] =
    useState(false);
  const [showCreatePunchListItemModal, setShowCreatePunchListItemModal] =
    useState(false);
  const [createdPunchListItemForLink, setCreatedPunchListItemForLink] =
    useState(null);
  const [createdRfiForLink, setCreatedRfiForLink] = useState(null);
  const [createdSubmittalForLink, setCreatedSubmittalForLink] = useState(null);
  /** Pending linked photo before publish: `{ file, objectUrl, filename }` or legacy `{ linkedPhoto, filename }` / `{ url }` */
  const [pendingPhotoRef, setPendingPhotoRef] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const photoLinkInputRef = useRef(null);
  /** Connection id → File for blob previews (upload on Save via multipart) */
  const connectionPhotoFilesRef = useRef(new Map());
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject?.project_id;
  const canDeletePersistedAnnotations = selectedProject?.role !== "consultant";
  const canUseSheetLinkDetection = selectedProject?.role !== "subcontractor";
  const [persistedAnnotationIds, setPersistedAnnotationIds] = useState(
    () => new Set(),
  );
  const queryClient = useQueryClient();
  const [lastSavedAnnotations, setLastSavedAnnotations] = useState([]);
  const [detectingSheetLinks, setDetectingSheetLinks] = useState(false);
  const [savingAnnotations, setSavingAnnotations] = useState(false);
  const [sheetLinkMenuId, setSheetLinkMenuId] = useState(null);
  const detectedAnnotationsRef = useRef(null);
  const skipUnsavedPromptRef = useRef(false);
  const [zoom, setZoom] = useState(1.0);
  const [visualZoom, setVisualZoom] = useState(1.0); // New state for visual scaling
  const [defaultZoom, setDefaultZoom] = useState(1.0);
  const zoomRef = useRef(1);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  useEffect(() => {
    setSheetLinkMenuId(null);
  }, [pageIndex, visualZoom]);
  const renderTaskRef = useRef(null);
  const modalContentRef = useRef(null);
  const [modalContentWidth, setModalContentWidth] = useState(1000);
  const [pdfPageOriginalWidth, setPdfPageOriginalWidth] = useState(null);
  const textInputRef = useRef("");
  /** Sync copy of text input position — cleared on commit to prevent blur+click double-save. */
  const textInputPosRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [isDrawingLoading, setIsDrawingLoading] = useState(false);
  const [cloudDrawingActive, setCloudDrawingActive] = useState(false);
  const [cloudStartPoint, setCloudStartPoint] = useState(null);
  const [activeCloudGroupId, setActiveCloudGroupId] = useState(null);
  const [touchStartDistance, setTouchStartDistance] = useState(null);
  const [touchStartZoom, setTouchStartZoom] = useState(null);
  const [touchStartCenter, setTouchStartCenter] = useState(null);
  const [touchStartScroll, setTouchStartScroll] = useState(null);
  const pdfContainerRef = useRef(null);
  /** After wheel or toolbar zoom: adjust scroll so (fx, fy) in the viewport stays fixed (see pinch in handleTouchEnd). */
  const zoomScrollAnchorRef = useRef(null);
  const renderRequestIdRef = useRef(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1000,
  );
  const [contentOverflows, setContentOverflows] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [showSearch, setShowSearch] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const searchDebounceTimer = useRef(null);
  const pdfTextCache = useRef(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({
    x: 0,
    y: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const removePendingRfiTagsNearBox = useCallback((boxStart, boxEnd, pIdx) => {
    const boxX = Math.min(boxStart.x, boxEnd.x);
    const boxY = Math.min(boxStart.y, boxEnd.y);
    const boxWidth = Math.abs(boxEnd.x - boxStart.x);
    const boxHeight = Math.abs(boxEnd.y - boxStart.y);
    const innerRectInset = CONNECTION_INNER_RECT_INSET;
    const centerX = boxX + boxWidth - innerRectInset;
    const centerY = boxY + boxHeight - innerRectInset;
    setRfiTags((prev) =>
      prev.filter((tag) => {
        if (tag.connectionId) return true;
        if (tag.page !== pIdx) return true;
        const dist = Math.sqrt(
          Math.pow(tag.connectionPoint.x - centerX, 2) +
            Math.pow(tag.connectionPoint.y - centerY, 2),
        );
        return dist >= 50;
      }),
    );
  }, []);

  const applyPhotoToConnectionDraft = useCallback(
    (photoRef) => {
      if (!connectionBoxStart || !connectionBoxEnd) return;
      removePendingRfiTagsNearBox(
        connectionBoxStart,
        connectionBoxEnd,
        pageIndex,
      );

      if (
        selectedAnnotationIndex !== null &&
        annotations[selectedAnnotationIndex]?.type === "connection"
      ) {
        const idx = selectedAnnotationIndex;
        const ann = annotations[idx];
        const fields = buildPhotoLinkFields(
          connectionBoxStart,
          connectionBoxEnd,
          pageIndex,
          ann.id,
          photoRef,
        );
        if (photoRef?.file instanceof File) {
          connectionPhotoFilesRef.current.set(ann.id, photoRef.file);
        } else {
          connectionPhotoFilesRef.current.delete(ann.id);
        }
        setAnnotations((prev) =>
          prev.map((a, i) => {
            if (i !== idx) return a;
            if (a.linkedPhotoPreviewUrl?.startsWith("blob:")) {
              try {
                URL.revokeObjectURL(a.linkedPhotoPreviewUrl);
              } catch (e) {}
            }
            return {
              ...a,
              type: "connection",
              ...fields,
              ...(fields.linkedPhotoUrl || fields.linkedPhotoPreviewUrl
                ? { linkedPhoto: undefined }
                : {}),
              linkedRfiId: null,
              linkedRfiTagId: null,
              rfiTag: null,
              targetDrawingId: undefined,
              targetDrawingNumber: undefined,
              targetFolderId: undefined,
              calloutKind: undefined,
              autoGenerated: undefined,
            };
          }),
        );
        setRfiTags((prev) =>
          prev.filter((t) => {
            if (ann.linkedRfiTagId && t.id === ann.linkedRfiTagId) return false;
            if (t.connectionId === ann.id) return false;
            return true;
          }),
        );
        setPendingPhotoRef(null);
      } else {
        setPendingPhotoRef((prev) => {
          if (prev?.objectUrl?.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(prev.objectUrl);
            } catch (e) {}
          }
          return photoRef;
        });
      }
    },
    [
      connectionBoxStart,
      connectionBoxEnd,
      pageIndex,
      selectedAnnotationIndex,
      annotations,
      removePendingRfiTagsNearBox,
    ],
  );

  useEffect(() => {
    if (visible && drawing?.file_url) {
      loadPdf(drawing.file_url);
    }
    if (visible && modalContentRef.current) {
      setModalContentWidth(modalContentRef.current.offsetWidth);
    }
  }, [visible, drawing?.file_url]);

  useEffect(() => {
    const handleResize = () => {
      if (modalContentRef.current) {
        setModalContentWidth(modalContentRef.current.offsetWidth);
      }
      if (typeof window !== "undefined") {
        setWindowWidth(window.innerWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (visible) {
      skipUnsavedPromptRef.current = false;
    }
    if (
      visible &&
      drawing?.annotations &&
      drawing.annotations === detectedAnnotationsRef.current
    ) {
      return;
    }
    if (visible && drawing?.annotations) {
      if (
        Array.isArray(drawing.annotations) &&
        drawing.annotations.length > 0
      ) {
        // Generate IDs once to ensure both states have the same IDs
        const annotationsWithIds = drawing.annotations.map((a) => ({
          ...a,
          id: a.id || uuidv4(),
        }));
        setAnnotations(annotationsWithIds);
        setLastSavedAnnotations(annotationsWithIds);
        setPersistedAnnotationIds(new Set(annotationIdsFrom(annotationsWithIds)));

        // Load RFI tags from annotations (they are stored in connection annotations' rfiTag property)
        const rfiTagsFromAnnotations = [];
        annotationsWithIds.forEach((ann) => {
          if (ann.type === "connection" && ann.rfiTag) {
            rfiTagsFromAnnotations.push(ann.rfiTag);
          }
        });
        setRfiTags(rfiTagsFromAnnotations);
      } else {
        setAnnotations([]);
        setLastSavedAnnotations([]);
        setPersistedAnnotationIds(new Set());
        setRfiTags([]);
      }
    } else if (visible) {
      setRfiTags([]);
    }
    if (!visible) {
      setSearchQuery("");
      setSearchResults([]);
      setCurrentMatchIndex(-1);
      setShowSearch(false);
      setPendingPhotoRef(null);
      setPhotoPreviewUrl(null);
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    }
  }, [visible, drawing?._id, drawing?.annotations]);

  const goToPreviousPage = useCallback(() => {
    setPageIndex((currentPageIndex) => Math.max(currentPageIndex - 1, 0));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageIndex((currentPageIndex) =>
      Math.min(currentPageIndex + 1, Math.max(numPages - 1, 0)),
    );
  }, [numPages]);

  const goToPage = useCallback(
    (index) => {
      setPageIndex(
        Math.max(0, Math.min(index, Math.max(numPages - 1, 0))),
      );
    },
    [numPages],
  );

  const handleSelectFolderDrawing = useCallback(
    (target, pageIndex = 0) => {
      const targetId = target?._id ?? target?.id ?? null;
      if (targetId != null && targetId === drawingId) {
        goToPage(pageIndex);
        return;
      }
      onSelectFolderDrawing?.(target, pageIndex);
    },
    [drawingId, goToPage, onSelectFolderDrawing],
  );

  useEffect(() => {
    if (!visible) return;

    const handleKeyDown = (e) => {
      const target = e.target;
      const isEditableTarget =
        target instanceof HTMLElement &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));

      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
      if (e.key === "Escape" && showSearch) {
        setShowSearch(false);
        setSearchQuery("");
        setSearchResults([]);
        setCurrentMatchIndex(-1);
      }
      if (
        !e.defaultPrevented &&
        !isEditableTarget &&
        !e.altKey &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        !showLinkModal &&
        !showRfiModal &&
        !showSubmittalModal &&
        !showCreateRfiModal &&
        !showCreateSubmittalModal &&
        !showCreatePunchListItemModal &&
        !showPunchListItemModal &&
        !photoPreviewUrl
      ) {
        const useDrawingNav =
          typeof onNavigatePreviousDrawing === "function" &&
          typeof onNavigateNextDrawing === "function";

        if (useDrawingNav && e.key === "PageUp") {
          e.preventDefault();
          goToPreviousPage();
          return;
        }
        if (useDrawingNav && e.key === "PageDown") {
          e.preventDefault();
          goToNextPage();
          return;
        }

        if (e.key === "ArrowLeft") {
          e.preventDefault();
          if (useDrawingNav) {
            onNavigatePreviousDrawing();
          } else {
            goToPreviousPage();
          }
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          if (useDrawingNav) {
            onNavigateNextDrawing();
          } else {
            goToNextPage();
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    visible,
    showSearch,
    showLinkModal,
    showRfiModal,
    showSubmittalModal,
    showCreateRfiModal,
    showCreateSubmittalModal,
    showCreatePunchListItemModal,
    showPunchListItemModal,
    photoPreviewUrl,
    goToPreviousPage,
    goToNextPage,
    onNavigatePreviousDrawing,
    onNavigateNextDrawing,
  ]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === "Delete" &&
        canDeleteAnnotationAt(selectedAnnotationIndex) &&
        !isAutoGeneratedDrawingLink(annotations[selectedAnnotationIndex])
      ) {
        deleteSelectedAnnotation();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedAnnotationIndex, annotations, persistedAnnotationIds, canDeletePersistedAnnotations]);

  // Height grows with wrapped lines; width stays fixed unless the user resizes.
  useEffect(() => {
    if (!showTextInput || isResizingTextRef.current) return;
    const { height } = getTextAnnotationMetrics(
      textInput || " ",
      fontSize,
      inputWidth,
    );
    const naturalHeight = Math.max(fontSize * 1.5, height + 8);
    setInputHeight(
      Math.max(naturalHeight, manualTextHeightRef.current || 0),
    );
  }, [textInput, showTextInput, fontSize, inputWidth]);

  useEffect(() => {
    showTextInputRef.current = showTextInput;
  }, [showTextInput]);

  /** Open/focus the text editor without the opening mousedown stealing focus back (blur→close). */
  const armTextEditorFocus = useCallback(() => {
    suppressTextBlurRef.current = true;
    requestAnimationFrame(() => {
      inputRef.current?.focus?.();
      setTimeout(() => {
        suppressTextBlurRef.current = false;
      }, 50);
    });
  }, []);

  const beginEditingTextAnnotation = useCallback(
    (ann, index, e) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      const boxW = resolveTextAnnotationBoxWidth(ann);
      const font = ann.fontSize || 16;
      const content = ann.content || "";
      setSelectedAnnotationIndex(index);
      setTextInput(content);
      textInputRef.current = content;
      textInputPosRef.current = ann.position;
      setTextInputPos(ann.position);
      setInputWidth(boxW);
      const naturalHeight =
        getTextAnnotationMetrics(content || " ", font, boxW).height + 8;
      manualTextHeightRef.current = ann.boxHeight ?? null;
      setInputHeight(Math.max(naturalHeight, ann.boxHeight || 0));
      setShowTextInput(true);
      showTextInputRef.current = true;
      calloutTipRef.current = ann.calloutTip || null;
      setCalloutTip(ann.calloutTip || null);
      calloutKneeRef.current = ann.calloutKnee || null;
      setCalloutKnee(ann.calloutKnee || null);
      setCurrentTool(ann.calloutTip ? TOOLS.CALLOUT : TOOLS.TEXT);
      setTextBold(ann.style?.fontWeight === "bold");
      setTextItalic(ann.style?.fontStyle === "italic");
      setTextUnderline(ann.style?.textDecoration?.includes("underline"));
      setTextStrike(ann.style?.textDecoration?.includes("line-through"));
      setFontSize(font);
      setColor(ann.color || "red");
      setTextHighlight(ann.style?.highlight || false);
      setTextSquiggly(ann.style?.squiggly || false);
      armTextEditorFocus();
    },
    [armTextEditorFocus],
  );

  useEffect(() => {
    const handlePaste = (e) => {
      if (currentTool === TOOLS.SELECT && pageViewport) {
        const pastedText = e.clipboardData.getData("text");
        if (pastedText) {
          const centerX = pageViewport.width / 2;
          const centerY = pageViewport.height / 2;
          addAnnotation({
            type: "text",
            content: pastedText,
            position: { x: centerX, y: centerY },
            color,
            fontSize,
            boxWidth: TEXT_ANNOTATION_DEFAULT_WIDTH,
            page: pageIndex,
            style: {},
          });
          message.success("Text pasted to PDF");
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [currentTool, pageViewport, color, fontSize, pageIndex]);

  // Update RFI tag positions to match connection points (bottom right corner)
  useEffect(() => {
    if (!visible || draggingRfiTag !== null) return;

    setRfiTags((prev) =>
      prev.map((tag) => {
        // Calculate connection point for this tag
        let connectionPt = tag.connectionPoint;

        if (tag.connectionId) {
          const connectionAnn = annotations.find(
            (ann) => ann.id === tag.connectionId,
          );
          if (connectionAnn) {
            const boxX = Math.min(connectionAnn.start.x, connectionAnn.end.x);
            const boxY = Math.min(connectionAnn.start.y, connectionAnn.end.y);
            const boxWidth = Math.abs(
              connectionAnn.end.x - connectionAnn.start.x,
            );
            const boxHeight = Math.abs(
              connectionAnn.end.y - connectionAnn.start.y,
            );
            connectionPt = {
              x: boxX + boxWidth - CONNECTION_INNER_RECT_INSET,
              y: boxY + boxHeight - CONNECTION_INNER_RECT_INSET,
            };
          }
        } else if (
          connectionBoxStart &&
          connectionBoxEnd &&
          showConnectionBox &&
          tag.page === pageIndex
        ) {
          const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
          const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
          const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
          const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
          connectionPt = {
            x: boxX + boxWidth - CONNECTION_INNER_RECT_INSET,
            y: boxY + boxHeight - CONNECTION_INNER_RECT_INSET,
          };
        }

        // Update tagPosition if it doesn't match - tag should be positioned so its bottom edge aligns with box bottom
        let expectedTagY = connectionPt.y;
        // Calculate box bottom Y if we have connection annotation or connection box
        if (tag.connectionId) {
          const connectionAnn = annotations.find(
            (ann) => ann.id === tag.connectionId,
          );
          if (connectionAnn) {
            const boxY = Math.min(connectionAnn.start.y, connectionAnn.end.y);
            const boxHeight = Math.abs(
              connectionAnn.end.y - connectionAnn.start.y,
            );
            expectedTagY =
              boxY +
              boxHeight -
              CONNECTION_INNER_RECT_INSET -
              RFI_TAG_HEIGHT / 2; // Center tag vertically at inner rect bottom
          }
        } else if (
          connectionBoxStart &&
          connectionBoxEnd &&
          showConnectionBox &&
          tag.page === pageIndex
        ) {
          const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
          const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
          expectedTagY =
            boxY + boxHeight - CONNECTION_INNER_RECT_INSET - RFI_TAG_HEIGHT / 2; // Center tag vertically at inner rect bottom
        }
        const expectedTagPosition = {
          x: connectionPt.x,
          y: expectedTagY,
        };
        if (
          Math.abs(tag.tagPosition.x - expectedTagPosition.x) > 1 ||
          Math.abs(tag.tagPosition.y - expectedTagPosition.y) > 1
        ) {
          return {
            ...tag,
            connectionPoint: connectionPt,
            tagPosition: expectedTagPosition,
          };
        }
        return tag;
      }),
    );
  }, [
    visible,
    annotations,
    connectionBoxStart,
    connectionBoxEnd,
    showConnectionBox,
    pageIndex,
  ]);

  // When link modal opens, set default selected link target if connection already has one
  useEffect(() => {
    if (!showLinkModal) return;

    if (linkType === "punchlist") {
      let punchListId = null;
      let itemId = null;
      if (selectedAnnotationIndex !== null) {
        const currentAnnotation = annotations[selectedAnnotationIndex];
        if (currentAnnotation?.type === "connection" && currentAnnotation.rfiTag?.type === "punchlist") {
          punchListId = currentAnnotation.rfiTag.punchListId || null;
          itemId = currentAnnotation.rfiTag.rfiId || null;
        }
      }
      if (!itemId && showConnectionBox && connectionBoxStart && connectionBoxEnd) {
        const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
        const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
        const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
        const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
        const innerRectInset = CONNECTION_INNER_RECT_INSET;
        const connectionCenter = {
          x: boxX + boxWidth - innerRectInset,
          y: boxY + boxHeight - innerRectInset,
        };
        const matchingTag = rfiTags.find((tag) => {
          if (tag.page !== pageIndex) return false;
          if (tag.connectionId) return false;
          if (tag.type !== "punchlist") return false;
          const distance = Math.sqrt(
            Math.pow(tag.connectionPoint.x - connectionCenter.x, 2) +
              Math.pow(tag.connectionPoint.y - connectionCenter.y, 2),
          );
          return distance < 50;
        });

        if (matchingTag) {
          punchListId = matchingTag.punchListId || null;
          itemId = matchingTag.rfiId || null;
        }
      }
      setLinkModalPunchListId(punchListId);
      setSelectedRfiId(itemId);
      return;
    }

    setLinkModalPunchListId(null);

    if (linkType === "drawing") {
      const current =
        selectedAnnotationIndex !== null
          ? annotations[selectedAnnotationIndex]
          : null;
      if (isDrawingLink(current)) {
        setSelectedRfiId(current.targetDrawingId || null);
        setDrawingSearch(current.targetDrawingNumber || "");
      } else {
        setSelectedRfiId(null);
      }
      return;
    }

    let existingRfiId = null;

    // Check if connection annotation exists and has an RFI/Submittal
    if (selectedAnnotationIndex !== null) {
      const currentAnnotation = annotations[selectedAnnotationIndex];
      if (currentAnnotation?.type === "connection") {
        existingRfiId = currentAnnotation.linkedRfiId;
      }
    }

    // If no RFI found in annotation, check rfiTags for unsaved connection box
    if (!existingRfiId && showConnectionBox && connectionBoxStart && connectionBoxEnd) {
      const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
      const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
      const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
      const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
      const innerRectInset = CONNECTION_INNER_RECT_INSET;
      const connectionCenter = {
        x: boxX + boxWidth - innerRectInset,
        y: boxY + boxHeight - innerRectInset,
      };

      const matchingTag = rfiTags.find((tag) => {
        if (tag.page !== pageIndex) return false;
        if (tag.connectionId) return false;
        if (tag.type === "punchlist") return false;
        const distance = Math.sqrt(
          Math.pow(tag.connectionPoint.x - connectionCenter.x, 2) +
            Math.pow(tag.connectionPoint.y - connectionCenter.y, 2),
        );
        return distance < 50;
      });

      if (matchingTag) {
        existingRfiId = matchingTag.rfiId;
      }
    }

    setSelectedRfiId(existingRfiId);
  }, [
    showLinkModal,
    linkType,
    selectedAnnotationIndex,
    annotations,
    showConnectionBox,
    connectionBoxStart,
    connectionBoxEnd,
    rfiTags,
    pageIndex,
  ]);

  // Fetch RFIs for link modal (simple list with only id, number, and title)
  const { data: rfisData, isLoading: rfisLoading } = useQuery({
    queryKey: ["rfis-simple", projectId],
    queryFn: () => fetchRFIsSimple(projectId),
    enabled: showLinkModal && linkType === "rfi" && !!projectId,
  });

  // Fetch Submittals for link modal (simple list with only id, number, and title)
  const { data: submittalsData, isLoading: submittalsLoading } = useQuery({
    queryKey: ["submittals-simple", projectId],
    queryFn: () => fetchSubmittalsSimple(projectId),
    enabled: showLinkModal && linkType === "submittal" && !!projectId,
  });

  const { data: punchListsData, isLoading: punchListsLoading } = useQuery({
    queryKey: ["punch-lists-drawing-link", projectId],
    queryFn: () => fetchPunchLists(projectId, 1, 200),
    enabled: showLinkModal && linkType === "punchlist" && !!projectId,
  });

  const { data: punchListItemsData, isLoading: punchListItemsLoading } = useQuery({
    queryKey: ["punch-list-items-drawing-link", linkModalPunchListId],
    queryFn: () => fetchPunchListItems(linkModalPunchListId, 1, 500, {}),
    enabled: showLinkModal && linkType === "punchlist" && !!linkModalPunchListId,
  });

  const drawingFolderId =
    drawing?.drawing_folder_id || drawing?.folder_id || drawing?.folderId || null;

  const { data: drawingsSearchData, isLoading: drawingsSearchLoading } = useQuery({
    queryKey: ["drawings-search-link", projectId, drawingFolderId, drawingSearch],
    queryFn: () =>
      searchDrawings(projectId, drawingSearch, {
        limit: 50,
        drawingFolderId,
      }),
    enabled: showLinkModal && linkType === "drawing" && !!projectId && !!drawingFolderId,
  });

  const drawingsForLink = drawingsSearchData?.drawings || [];

  const punchListItemsForLink = (() => {
    const items = punchListItemsData?.items || [];
    const createdItemId =
      createdPunchListItemForLink?.id ?? createdPunchListItemForLink?._id;
    if (!createdItemId) {
      return items;
    }
    const alreadyLoaded = items.some(
      (it) => String(it.id ?? it._id) === String(createdItemId),
    );
    return alreadyLoaded ? items : [createdPunchListItemForLink, ...items];
  })();

  const rfisForLink = (() => {
    const items = rfisData?.rfis || [];
    const createdItemId = createdRfiForLink?.id ?? createdRfiForLink?._id;
    if (!createdItemId) {
      return items;
    }
    const alreadyLoaded = items.some(
      (rfi) => String(rfi.id ?? rfi._id) === String(createdItemId),
    );
    return alreadyLoaded ? items : [createdRfiForLink, ...items];
  })();

  const submittalsForLink = (() => {
    const items = submittalsData?.submittals || [];
    const createdItemId =
      createdSubmittalForLink?.id ?? createdSubmittalForLink?._id;
    if (!createdItemId) {
      return items;
    }
    const alreadyLoaded = items.some(
      (submittal) =>
        String(submittal.id ?? submittal._id) === String(createdItemId),
    );
    return alreadyLoaded ? items : [createdSubmittalForLink, ...items];
  })();

  const drawingLinkInitialRfiData = useMemo(() => {
    if (!showCreateRfiModal) {
      return null;
    }
    const trimmedText = connectionText?.trim() || "";
    return {
      ...(trimmedText
        ? {
            question: trimmedText,
            title: trimmedText.slice(0, 80),
          }
        : {}),
    };
  }, [showCreateRfiModal, connectionText]);

  const drawingLinkInitialSubmittalData = useMemo(() => {
    if (!showCreateSubmittalModal) {
      return null;
    }
    const trimmedText = connectionText?.trim() || "";
    return {
      ...(trimmedText
        ? {
            description: trimmedText,
            title: trimmedText.slice(0, 80),
          }
        : {}),
      steps: [{ reviews: [] }],
    };
  }, [showCreateSubmittalModal, connectionText]);

  const filterOption = (input, option) => {
    return (option?.label ?? "").toLowerCase().includes(input.toLowerCase());
  };

  // Handle connection box moving from toolbar
  useEffect(() => {
    if (!isMovingConnection) return;

    const handleMouseMove = (e) => {
      if (!isMovingConnection || !connectionBoxStart || !connectionBoxEnd)
        return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      let clientX = e.clientX;
      let clientY = e.clientY;
      if (e.touches && e.touches.length > 0) {
        clientX = e.touches[0].clientX;
        clientY = e.touches[0].clientY;
      }

      const pt = {
        x: (clientX - rect.left) / zoom,
        y: (clientY - rect.top) / zoom,
      };

      const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
      const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
      const newStart = {
        x: pt.x - connectionMoveOffset.x,
        y: pt.y - connectionMoveOffset.y,
      };
      setConnectionBoxStart(newStart);
      setConnectionBoxEnd({
        x: newStart.x + boxWidth,
        y: newStart.y + boxHeight,
      });
    };

    const handleMouseUp = () => {
      setIsMovingConnection(false);
      setIsDrawing(false);
      setConnectionMoveOffset({ x: 0, y: 0 });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleMouseMove);
    document.addEventListener("touchend", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleMouseMove);
      document.removeEventListener("touchend", handleMouseUp);
    };
  }, [
    isMovingConnection,
    connectionBoxStart,
    connectionBoxEnd,
    connectionMoveOffset,
    zoom,
  ]);

  useEffect(() => {
    if (pdfDoc && typeof pageIndex === "number") {
      renderPage(pdfDoc, pageIndex, zoom);
    }
    // pdfDoc must be a dependency: when the PDF finishes loading, zoom/pageIndex often
    // do not change, so without pdfDoc this effect never runs and loading never clears.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDoc, zoom, pageIndex]);

  useEffect(() => {
    if (pdfDoc && typeof pageIndex === "number" && searchResults.length > 0) {
      const timer = setTimeout(() => {
        renderPage(pdfDoc, pageIndex, zoom);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [searchResults, currentMatchIndex]);

  useEffect(() => {
    if (pdfDoc && numPages > 0 && !pdfTextCache.current) {
      extractAndCacheTextContent();
    }
  }, [pdfDoc, numPages]);

  useEffect(() => {
    if (pdfPageOriginalWidth && modalContentRef.current) {
      const fitZoom = calculateFitZoom(pdfPageOriginalWidth);
      setDefaultZoom(fitZoom);
      setZoom(fitZoom);
      setVisualZoom(fitZoom); // Initialize visualZoom
    }
    // eslint-disable-next-line
  }, [pdfPageOriginalWidth, numPages, modalContentRef.current]);

  useEffect(() => {
    if (!pdfPageOriginalWidth || !modalContentRef.current) return;
    setDefaultZoom(calculateFitZoom(pdfPageOriginalWidth));
    // eslint-disable-next-line
  }, [pageThumbsOpen]);

  // Check if content overflows container to enable scrolling
  useEffect(() => {
    if (pdfContainerRef.current && pageViewport) {
      const container = pdfContainerRef.current;
      const overflowsX = pageViewport.width > container.clientWidth;
      const overflowsY = pageViewport.height > container.clientHeight;
      setContentOverflows(overflowsX || overflowsY);
    } else {
      setContentOverflows(false);
    }
  }, [pageViewport, windowWidth]);

  // Handle Ctrl/Cmd + Scroll for zoom
  useEffect(() => {
    if (!visible || !pdfContainerRef.current) return;

    const handleWheel = (e) => {
      // Zoom only with Ctrl/Cmd or while Pan is active (Select: normal scroll, no wheel-zoom)
      if (e.ctrlKey || e.metaKey || currentTool === TOOLS.PAN) {
        e.preventDefault();

        // Negative deltaY means scroll up (zoom in), positive means scroll down (zoom out)
        const zoomDelta = e.deltaY < 0 ? 0.1 : -0.1;
        const prev = zoomRef.current;
        const newZoom = Math.min(Math.max(prev + zoomDelta, 0.1), 4);
        if (newZoom === prev) return;

        const container = pdfContainerRef.current;
        if (container) {
          const rect = container.getBoundingClientRect();
          zoomScrollAnchorRef.current = {
            fx: e.clientX - rect.left,
            fy: e.clientY - rect.top,
            oldZoom: prev,
            newZoom,
          };
        }

        zoomRef.current = newZoom;
        setZoom(newZoom);
        setVisualZoom(newZoom);
      }
    };

    const container = pdfContainerRef.current;
    container.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, [visible, currentTool]);

  // After wheel/toolbar zoom + PDF render: keep anchor point fixed (same math as pinch in handleTouchEnd).
  useLayoutEffect(() => {
    if (!visible || isDrawingLoading) return;
    const anchor = zoomScrollAnchorRef.current;
    if (!anchor || !pdfContainerRef.current || !pageViewport) return;
    if (anchor.newZoom !== zoom) return;

    zoomScrollAnchorRef.current = null;
    const container = pdfContainerRef.current;
    const { fx, fy, oldZoom, newZoom } = anchor;
    if (oldZoom <= 0 || newZoom === oldZoom) return;

    const zoomRatio = newZoom / oldZoom;
    const sl0 = container.scrollLeft;
    const st0 = container.scrollTop;
    const contentX = sl0 + fx;
    const contentY = st0 + fy;
    let newSl = contentX * zoomRatio - fx;
    let newSt = contentY * zoomRatio - fy;

    const maxL = Math.max(0, pageViewport.width - container.clientWidth);
    const maxT = Math.max(0, pageViewport.height - container.clientHeight);
    container.scrollLeft = Math.max(0, Math.min(maxL, newSl));
    container.scrollTop = Math.max(0, Math.min(maxT, newSt));
  }, [visible, zoom, pageViewport, isDrawingLoading]);

  const calculateFitZoom = (pageWidth) => {
    if (modalContentRef.current) {
      let sidebarWidth = 0;
      if (folderDrawings.length >= 1) {
        sidebarWidth = pageThumbsOpen
          ? PAGE_THUMBNAIL_SIDEBAR_WIDTH
          : PAGE_THUMBNAIL_COLLAPSED_WIDTH;
      }
      let fitZoom =
        (modalContentRef.current.offsetWidth - sidebarWidth - 32) / pageWidth;
      fitZoom = Math.min(Math.max(fitZoom, 0.1), 4);
      return fitZoom;
    }
    return 1.0;
  };

  const loadPdf = async (url) => {
    setIsDrawingLoading(true);
    try {
      const loadingTask = pdfjsLib.getDocument(url);
      const loadedPdf = await loadingTask.promise;
      setPdfDoc(loadedPdf);
      setNumPages(loadedPdf.numPages);
      const startPage = Math.max(
        0,
        Math.min(
          Number.isFinite(initialPageIndex) ? initialPageIndex : 0,
          Math.max(loadedPdf.numPages - 1, 0),
        ),
      );
      setPageIndex(startPage);
      const page = await loadedPdf.getPage(startPage + 1);
      const baseViewport = page.getViewport({ scale: 1 });
      setPdfPageOriginalWidth(baseViewport.width);
      pdfTextCache.current = null;
    } catch (error) {
      setIsDrawingLoading(false);
      throw error;
    }
  };

  const extractAndCacheTextContent = async () => {
    if (!pdfDoc || pdfTextCache.current) return;

    try {
      const cache = [];
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        const viewport = page.getViewport({ scale: 1 });

        let fullText = "";
        const textItems = [];

        for (
          let itemIndex = 0;
          itemIndex < textContent.items.length;
          itemIndex++
        ) {
          const item = textContent.items[itemIndex];
          if (item.str) {
            fullText += item.str;
            textItems.push({
              ...item,
              startIndex: fullText.length - item.str.length,
              endIndex: fullText.length,
            });
          }
        }

        cache.push({
          pageNum,
          fullText,
          textItems,
          viewport,
        });
      }
      pdfTextCache.current = cache;
    } catch (error) {
      console.error("Error extracting text content:", error);
    }
  };

  const renderPage = async (pdf, index, customZoom = null) => {
    const requestId = renderRequestIdRef.current + 1;
    renderRequestIdRef.current = requestId;
    setIsDrawingLoading(true);

    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) {}
      renderTaskRef.current = null;
    }
    let page;
    try {
      page = await pdf.getPage(index + 1);
    } catch (error) {
      if (renderRequestIdRef.current === requestId) {
        setIsDrawingLoading(false);
      }
      throw error;
    }
    const baseViewport = page.getViewport({ scale: 1 });
    setPdfPageOriginalWidth(baseViewport.width);
    const useZoom = customZoom !== null ? customZoom : zoom;
    const viewport = page.getViewport({ scale: useZoom });
    setPageViewport(viewport);

    const canvas = canvasRef.current;
    if (!canvas) {
      if (renderRequestIdRef.current === requestId) {
        setIsDrawingLoading(false);
      }
      return;
    }
    const context = canvas.getContext("2d");
    const devicePixelRatio =
      typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const maxCanvasPixels = 80_000_000;
    const maxScaleForCanvas = Math.sqrt(
      maxCanvasPixels / Math.max(viewport.width * viewport.height, 1),
    );
    const outputScale = Math.max(
      1,
      Math.min(devicePixelRatio * 1.5, 3, maxScaleForCanvas),
    );

    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;

    let renderTask;
    try {
      renderTask = page.render({
        canvasContext: context,
        viewport,
        transform:
          outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null,
      });
    } catch (syncErr) {
      console.error("PDF page.render failed (sync):", syncErr);
      if (renderRequestIdRef.current === requestId) {
        renderTaskRef.current = null;
        setIsDrawingLoading(false);
      }
      return;
    }
    renderTaskRef.current = renderTask;
    try {
      await renderTask.promise;
    } catch (err) {
      if (!(err && err.name === "RenderingCancelledException")) {
        console.error(err);
      }
    } finally {
      if (renderRequestIdRef.current === requestId) {
        renderTaskRef.current = null;
        setIsDrawingLoading(false);
      }
    }
  };

  // PDF text search function (uses cached text content)
  const searchInPdf = async (query) => {
    if (!pdfDoc || !query.trim()) {
      setSearchResults([]);
      setCurrentMatchIndex(-1);
      return;
    }

    setIsSearching(true);
    const results = [];
    const searchText = query.trim();
    const searchRegex = new RegExp(
      searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "gi",
    );

    try {
      // If cache is not ready, extract text on-demand (fallback)
      if (!pdfTextCache.current) {
        await extractAndCacheTextContent();
      }

      const cache = pdfTextCache.current || [];

      for (const pageData of cache) {
        const { pageNum, fullText, textItems, viewport } = pageData;

        let match;
        searchRegex.lastIndex = 0; // Reset regex state
        while ((match = searchRegex.exec(fullText)) !== null) {
          const matchStart = match.index;
          const matchEnd = match.index + match[0].length;
          const matchText = match[0];
          const relevantItems = textItems.filter(
            (item) => item.endIndex > matchStart && item.startIndex < matchEnd,
          );

          if (relevantItems.length > 0) {
            const firstItem = relevantItems[0];
            const transform = firstItem.transform;
            const fontSize = Math.abs(transform[0]);

            let matchX = transform[4];
            let matchWidth = 0;

            if (relevantItems.length === 1) {
              const item = firstItem;
              const itemText = item.str;
              const itemStartInFullText = item.startIndex;

              const matchStartInItem = matchStart - itemStartInFullText;
              const matchEndInItem = matchEnd - itemStartInFullText;

              const itemWidth = item.width || fontSize * itemText.length * 0.6;
              const charWidth = itemWidth / itemText.length;

              matchX = transform[4] + matchStartInItem * charWidth;
              matchWidth = (matchEndInItem - matchStartInItem) * charWidth;
            } else {
              const firstItemText = firstItem.str;
              const matchStartInFirstItem = matchStart - firstItem.startIndex;
              const firstItemWidth =
                firstItem.width || fontSize * firstItemText.length * 0.6;
              const firstCharWidth = firstItemWidth / firstItemText.length;

              matchX = transform[4] + matchStartInFirstItem * firstCharWidth;

              for (let i = 0; i < relevantItems.length; i++) {
                const item = relevantItems[i];
                const itemText = item.str;
                const itemStart = item.startIndex;
                const itemEnd = item.endIndex;

                let itemMatchStart = Math.max(0, matchStart - itemStart);
                let itemMatchEnd = Math.min(
                  itemText.length,
                  matchEnd - itemStart,
                );

                if (itemMatchEnd > itemMatchStart) {
                  const itemWidth =
                    item.width || fontSize * itemText.length * 0.6;
                  const charWidth = itemWidth / itemText.length;
                  matchWidth += (itemMatchEnd - itemMatchStart) * charWidth;
                }
              }
            }

            const y = transform[5];
            const height = fontSize || 12;
            const svgY = viewport.height - y;

            results.push({
              page: pageNum - 1,
              x: matchX,
              y: svgY,
              width: matchWidth || matchText.length * fontSize * 0.6,
              height: height,
              text: matchText,
            });
          }
        }
      }

      setSearchResults(results);
      if (results.length > 0) {
        setCurrentMatchIndex(0);
        navigateToMatch(0, results);
      } else {
        setCurrentMatchIndex(-1);
      }
    } catch (error) {
      console.error("Error searching PDF:", error);
      message.error("Error searching PDF");
    } finally {
      setIsSearching(false);
    }
  };

  const navigateToMatch = (index, results = searchResults) => {
    if (index < 0 || index >= results.length) return;

    const match = results[index];
    setCurrentMatchIndex(index);
    setPageIndex(match.page);
    setTimeout(() => {
      if (pdfContainerRef.current && svgRef.current) {
        const container = pdfContainerRef.current;
        const svg = svgRef.current;
        const rect = svg.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        // Calculate scroll position
        const scrollX = match.x * zoom - containerRect.width / 2 + rect.left;
        const scrollY = match.y * zoom - containerRect.height / 2 + rect.top;

        container.scrollTo({
          left: Math.max(0, scrollX),
          top: Math.max(0, scrollY),
          behavior: "smooth",
        });
      }
    }, 300);
  };

  // Navigate to next match
  const goToNextMatch = () => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % searchResults.length;
    navigateToMatch(nextIndex);
  };

  // Navigate to previous match
  const goToPreviousMatch = () => {
    if (searchResults.length === 0) return;
    const prevIndex =
      currentMatchIndex <= 0 ? searchResults.length - 1 : currentMatchIndex - 1;
    navigateToMatch(prevIndex);
  };

  // Handle search input change with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }

    if (value.trim()) {
      searchDebounceTimer.current = setTimeout(() => {
        searchInPdf(value);
      }, 300);
    } else {
      setSearchResults([]);
      setCurrentMatchIndex(-1);
      setIsSearching(false);
    }
  };

  const getMousePos = (e) => {
    const svg = svgRef.current;
    if (!svg || !pageViewport) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    let clientX, clientY;
    if (e.clientX !== undefined && e.clientY !== undefined) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else if (e.touches && e.touches.length > 0) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else if (e.changedTouches && e.changedTouches.length > 0) {
      clientX = e.changedTouches[0].clientX;
      clientY = e.changedTouches[0].clientY;
    } else {
      clientX = 0;
      clientY = 0;
    }
    return {
      x: (clientX - rect.left) / zoom,
      y: (clientY - rect.top) / zoom,
    };
  };

  const handleToolClick = (tool) => {
    const textEditorTool = calloutTipRef.current ? TOOLS.CALLOUT : TOOLS.TEXT;
    if (showTextInput && tool !== textEditorTool) {
      // Commit instead of discard. Toolbar mousedown preventDefault keeps focus in
      // the textarea, so blur skips commit when focus stays in the toolbar.
      addTextAnnotation(textInputRef.current || textInput);
    }
    if (showConnectionBox && tool !== TOOLS.CONNECTION) {
      setShowConnectionBox(false);
      setConnectionBoxStart(null);
      setConnectionBoxEnd(null);
      setConnectionText("");
    }
    if (cloudDrawingActive && tool !== TOOLS.SHAPE) {
      finalizeCloudDrawing();
    }
    setCurrentTool((prev) => (prev === tool ? TOOLS.PAN : tool));
  };

  const handleDetectSheetLinks = async () => {
    if (!projectId || !drawingId || detectingSheetLinks) return;
    if (savingAnnotations) {
      message.warning("Please wait until the annotations are saved");
      return;
    }
    setDetectingSheetLinks(true);
    try {
      const result = await detectSheetLinks(projectId, drawingId, pageIndex);
      const added = result?.added || [];
      const serverAnns = result?.annotations || [];
      if (Array.isArray(result?.annotations)) {
        const synced = queryClient.setQueryData(
          ["drawing", projectId, drawing?._id],
          (old) => (old ? { ...old, annotations: serverAnns } : old),
        );
        detectedAnnotationsRef.current = synced?.annotations ?? null;
      }
      setAnnotations((prev) =>
        applyResolvedSheetLinkTargets(mergeDetectedSheetLinks(prev, added), serverAnns),
      );
      setLastSavedAnnotations((prev) =>
        applyResolvedSheetLinkTargets(mergeDetectedSheetLinks(prev, added), serverAnns),
      );
      setPersistedAnnotationIds(
        (prev) => new Set([...prev, ...annotationIdsFrom(added)]),
      );
      if (!added.length) {
        message.info("No sheet links found on this page");
        return;
      }
      message.success(
        added.length === 1
          ? "1 sheet link added"
          : `${added.length} sheet links added`,
      );
    } catch (err) {
      console.error(err);
      message.error(err?.response?.data?.detail || "Could not detect sheet links");
    } finally {
      setDetectingSheetLinks(false);
    }
  };

  const openNewTextEditor = (pt, tip = null) => {
    setTextInput("");
    textInputRef.current = "";
    textInputPosRef.current = pt;
    setTextInputPos(pt);
    setInputWidth(TEXT_ANNOTATION_DEFAULT_WIDTH);
    setInputHeight(24);
    manualTextHeightRef.current = null;
    setShowTextInput(true);
    showTextInputRef.current = true;
    calloutTipRef.current = tip;
    setCalloutTip(tip);
    calloutKneeRef.current = null;
    setCalloutKnee(null);
    setSelectedAnnotationIndex(null);
    setTextBold(false);
    setTextItalic(false);
    setTextUnderline(false);
    setTextStrike(false);
    setFontSize(16);
    armTextEditorFocus();
  };

  const startDrawing = (e) => {
    setSheetLinkMenuId(null);
    if (
      showTextInputRef.current &&
      textInputPosRef.current &&
      e &&
      (() => {
        const pt = getMousePos(e);
        const pos = textInputPosRef.current;
        const x1 = pos.x - inputWidth / 2;
        const x2 = pos.x + inputWidth / 2;
        const y1 = pos.y - inputHeight / 2;
        const y2 = pos.y + inputHeight / 2;
        return pt.x >= x1 && pt.x <= x2 && pt.y >= y1 && pt.y <= y2;
      })()
    ) {
      return true;
    }
    if (showTextInputRef.current) {
      // Commits text, or deletes the annotation when the editor was cleared empty.
      addTextAnnotation(textInputRef.current || textInput);
      // Same click finishes the current text — don't open a new box on top of it.
      return true;
    }

    // Check if clicking on RFI tag
    if (e.target.getAttribute("data-rfi-tag-id")) {
      const tagId = e.target.getAttribute("data-rfi-tag-id");
      const pt = getMousePos(e);
      const tag = rfiTags.find((t) => t.id === tagId && t.page === pageIndex);
      if (tag) {
        setDraggingRfiTag(tagId);
        setDragOffset({
          x: pt.x - tag.tagPosition.x,
          y: pt.y - tag.tagPosition.y,
        });
        return true;
      }
    }

    // Resolve annotation id from the target or a parent (e.g. tspan inside text).
    const clickedDataId =
      e.target.getAttribute?.("data-id") ||
      (typeof e.target.closest === "function"
        ? e.target.closest("[data-id]")?.getAttribute("data-id")
        : null);
    if (clickedDataId) {
      const index = annotations.findIndex(
        (ann) => ann.page === pageIndex && ann.id === clickedDataId,
      );
      if (index !== -1) {
        const ann = annotations[index];
        if (isAutoGeneratedDrawingLink(ann)) {
          setSelectedAnnotationIndex(null);
          setSheetLinkMenuId(ann.id);
          return true;
        }
        // If connection tool is active and clicking on connection, edit it
        if (
          currentTool === TOOLS.CONNECTION &&
          ann.type === "connection"
        ) {
          setSelectedAnnotationIndex(index);
          setConnectionBoxStart(ann.start);
          setConnectionBoxEnd(ann.end);
          setConnectionText(ann.content || "");
          setShowConnectionBox(true);
          return true;
        }
        const calloutPart = ann.calloutTip
          ? e.target.getAttribute?.("data-callout-part")
          : null;
        // Text tool: edit the clicked text instead of creating a new one on top.
        if (
          ann.type === "text" &&
          !calloutPart &&
          (currentTool === TOOLS.TEXT ||
            currentTool === TOOLS.CALLOUT ||
            showTextInputRef.current)
        ) {
          beginEditingTextAnnotation(ann, index, e);
          return true;
        }
        setDraggingIndex(index);
        draggingCalloutPartRef.current = calloutPart || null;
        const pt = getMousePos(e);
        let offset = { x: 0, y: 0 };
        const kneePoint = calloutPart === "knee" ? getCalloutKneePoint(ann) : null;
        if (calloutPart?.startsWith("resize-")) {
          calloutResizeStartRef.current = getCalloutBox(ann);
        } else if (kneePoint) {
          offset = { x: pt.x - kneePoint.x, y: pt.y - kneePoint.y };
        } else if (ann.type === "text" && calloutPart === "tip") {
          offset = { x: pt.x - ann.calloutTip.x, y: pt.y - ann.calloutTip.y };
        } else if (ann.type === "text") {
          offset = { x: pt.x - ann.position.x, y: pt.y - ann.position.y };
        } else if (
          ann.type === "shape" ||
          ann.type === "highlight" ||
          ann.type === "squiggly" ||
          ann.type === "connection"
        ) {
          if (
            ann.type === "shape" &&
            ann.shapeType === SHAPES.CLOUD &&
            ann.segments?.length
          ) {
            offset = {
              x: pt.x - ann.segments[0].start.x,
              y: pt.y - ann.segments[0].start.y,
            };
          } else {
            offset = { x: pt.x - ann.start.x, y: pt.y - ann.start.y };
          }
        } else if (ann.type === "pen") {
          const match = ann.path.match(/M (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)/);
          if (match) {
            offset = {
              x: pt.x - parseFloat(match[1]),
              y: pt.y - parseFloat(match[2]),
            };
          }
        }
        setDragOffset(offset);
        return true;
      }
      return true;
    }
    if (currentTool === TOOLS.TEXT || showTextInputRef.current) {
      const pt = getMousePos(e);
      e?.preventDefault?.();
      e?.stopPropagation?.();
      openNewTextEditor(pt);
      return true;
    }
    if (currentTool === TOOLS.CALLOUT) {
      const pt = getMousePos(e);
      e?.preventDefault?.();
      setIsDrawing(true);
      setStartPoint(pt);
      setCalloutDragEnd(pt);
      setSelectedAnnotationIndex(null);
      return true;
    }
    if (currentTool === TOOLS.CONNECTION) {
      const pt = getMousePos(e);

      if (!showConnectionBox) {
        const clickedAnnotation = annotations.find((ann) => {
          if (
            ann.type !== "connection" ||
            ann.page !== pageIndex ||
            isAutoGeneratedDrawingLink(ann)
          ) {
            return false;
          }
          const boxX = Math.min(ann.start.x, ann.end.x);
          const boxY = Math.min(ann.start.y, ann.end.y);
          const boxWidth = Math.abs(ann.end.x - ann.start.x);
          const boxHeight = Math.abs(ann.end.y - ann.start.y);
          return (
            pt.x >= boxX &&
            pt.x <= boxX + boxWidth &&
            pt.y >= boxY &&
            pt.y <= boxY + boxHeight
          );
        });
        if (clickedAnnotation) {
          const index = annotations.findIndex(
            (ann) => ann.id === clickedAnnotation.id,
          );
          setSelectedAnnotationIndex(index);
          setConnectionBoxStart(clickedAnnotation.start);
          setConnectionBoxEnd(clickedAnnotation.end);
          setConnectionText(clickedAnnotation.content || "");
          setShowConnectionBox(true);
          return;
        }
      }

      // Check if clicking on resize handle
      if (showConnectionBox && connectionBoxStart && connectionBoxEnd) {
        const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
        const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
        const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
        const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
        const handleSize = 8;

        // Check corners
        const corners = [
          { x: boxX, y: boxY, handle: "nw" },
          { x: boxX + boxWidth, y: boxY, handle: "ne" },
          { x: boxX, y: boxY + boxHeight, handle: "sw" },
          { x: boxX + boxWidth, y: boxY + boxHeight, handle: "se" },
        ];

        for (const corner of corners) {
          if (
            pt.x >= corner.x - handleSize &&
            pt.x <= corner.x + handleSize &&
            pt.y >= corner.y - handleSize &&
            pt.y <= corner.y + handleSize
          ) {
            setIsResizingConnection(true);
            setResizeHandle(corner.handle);
            return;
          }
        }

        // Check if clicking inside the box (including toolbar)
        if (
          pt.x >= boxX &&
          pt.x <= boxX + boxWidth &&
          pt.y >= boxY &&
          pt.y <= boxY + boxHeight
        ) {
          // Start moving the box (from anywhere including toolbar)
          setIsMovingConnection(true);
          setConnectionMoveOffset({
            x: pt.x - boxX,
            y: pt.y - boxY,
          });
          return;
        }

      }

      // Start drawing connection box (like rectangle)
      setIsDrawing(true);
      setStartPoint(pt);
      setConnectionBoxStart(pt);
      setConnectionBoxEnd(pt);
      setShowConnectionBox(false); // Don't show until user finishes drawing
      setConnectionText("");
      setSelectedAnnotationIndex(null);
      setPendingPhotoRef(null);
      return;
    }
    if (currentTool === TOOLS.SHAPE && currentShape === SHAPES.CLOUD) {
      if (!cloudDrawingActive) {
        setCloudDrawingActive(true);
        setActiveCloudGroupId(uuidv4());
        const pt = getMousePos(e);
        setIsDrawing(true);
        setStartPoint(pt);
        setCloudStartPoint(pt);
        setCurrentPath({ start: pt, end: pt });
        setSelectedAnnotationIndex(null);
      } else {
        const pt = getMousePos(e);
        if (
          cloudStartPoint &&
          distance(pt, cloudStartPoint) < CLOUD_CLOSE_DISTANCE
        ) {
          if (isDrawing && startPoint) {
            finalizeCloudDrawing({ start: startPoint, end: cloudStartPoint });
          } else {
            finalizeCloudDrawing();
          }
          if (e && typeof e.stopPropagation === "function") e.stopPropagation();
          if (e && typeof e.preventDefault === "function") e.preventDefault();
          return true;
        }
        setIsDrawing(true);
        setStartPoint(pt);
        setCurrentPath({ start: pt, end: pt });
        setSelectedAnnotationIndex(null);
      }
      return;
    }
    if (currentTool === TOOLS.PAN) {
      setIsPanning(true);
      setPanStart({
        x: e.clientX || (e.touches && e.touches[0].clientX),
        y: e.clientY || (e.touches && e.touches[0].clientY),
        scrollLeft: pdfContainerRef.current?.scrollLeft || 0,
        scrollTop: pdfContainerRef.current?.scrollTop || 0,
      });
      return;
    }
    if (currentTool === TOOLS.SELECT) {
      setSelectedAnnotationIndex(null);
      return;
    }
    const pt = getMousePos(e);
    setIsDrawing(true);
    setStartPoint(pt);
    if (currentTool === TOOLS.PEN) {
      setCurrentPath(`M ${pt.x} ${pt.y}`);
    }
    setSelectedAnnotationIndex(null);
  };

  const draw = (e) => {
    if (draggingRfiTag !== null) {
      const pt = getMousePos(e);
      setRfiTags((prev) =>
        prev.map((tag) =>
          tag.id === draggingRfiTag
            ? {
                ...tag,
                tagPosition: {
                  x: pt.x - dragOffset.x,
                  y: pt.y - dragOffset.y,
                },
              }
            : tag,
        ),
      );
      return;
    }
    if (draggingIndex !== null) {
      const pt = getMousePos(e);
      const draggedAnnotation = annotations[draggingIndex];

      if (
        draggedAnnotation?.shapeType === SHAPES.CLOUD &&
        draggedAnnotation?.cloudGroupId
      ) {
        const groupId = draggedAnnotation.cloudGroupId;
        const dx = pt.x - dragOffset.x - draggedAnnotation.start.x;
        const dy = pt.y - dragOffset.y - draggedAnnotation.start.y;
        setAnnotations((prev) =>
          prev.map((ann) => {
            if (ann.cloudGroupId !== groupId) return ann;
            return {
              ...ann,
              start: { x: ann.start.x + dx, y: ann.start.y + dy },
              end: { x: ann.end.x + dx, y: ann.end.y + dy },
            };
          }),
        );
        return;
      }

      const calloutPart = draggingCalloutPartRef.current;
      const calloutResizeStart = calloutResizeStartRef.current;
      setAnnotations((prev) =>
        prev.map((ann, i) => {
          if (i !== draggingIndex) return ann;
          if (ann.type === "text") {
            const next = { x: pt.x - dragOffset.x, y: pt.y - dragOffset.y };
            if (calloutPart === "tip" && ann.calloutTip) {
              return { ...ann, calloutTip: next };
            }
            if (calloutPart === "knee" && ann.calloutTip) {
              return { ...ann, calloutKnee: next };
            }
            if (calloutPart?.startsWith("resize-") && calloutResizeStart) {
              return {
                ...ann,
                ...resizeCalloutBox(
                  calloutResizeStart,
                  calloutPart.slice("resize-".length),
                  pt,
                  ann,
                ),
              };
            }
            const dx = next.x - ann.position.x;
            const dy = next.y - ann.position.y;
            const shift = (p) => (p ? { x: p.x + dx, y: p.y + dy } : p);
            if (calloutPart === "leader" && ann.calloutTip) {
              return {
                ...ann,
                position: next,
                calloutTip: shift(ann.calloutTip),
                calloutKnee: shift(ann.calloutKnee),
              };
            }
            if (ann.calloutKnee) {
              return { ...ann, position: next, calloutKnee: shift(ann.calloutKnee) };
            }
            return { ...ann, position: next };
          } else if (
            ann.type === "shape" &&
            ann.shapeType === SHAPES.CLOUD &&
            ann.segments?.length
          ) {
            const refStart = ann.segments[0].start;
            const dx = pt.x - dragOffset.x - refStart.x;
            const dy = pt.y - dragOffset.y - refStart.y;
            const moved = {
              ...ann,
              segments: ann.segments.map((segment) => ({
                start: {
                  x: segment.start.x + dx,
                  y: segment.start.y + dy,
                },
                end: {
                  x: segment.end.x + dx,
                  y: segment.end.y + dy,
                },
              })),
            };
            // Keep legacy top-level start/end in sync for bounds/hit-testing/backward compat
            if (ann.start) {
              moved.start = { x: ann.start.x + dx, y: ann.start.y + dy };
            }
            if (ann.end) {
              moved.end = { x: ann.end.x + dx, y: ann.end.y + dy };
            }
            return moved;
          } else if (
            ann.type === "shape" ||
            ann.type === "highlight" ||
            ann.type === "squiggly" ||
            ann.type === "connection"
          ) {
            const dx = pt.x - dragOffset.x - ann.start.x;
            const dy = pt.y - dragOffset.y - ann.start.y;
            const updatedAnn = {
              ...ann,
              start: { x: ann.start.x + dx, y: ann.start.y + dy },
              end: { x: ann.end.x + dx, y: ann.end.y + dy },
            };
            // Update RFI tags' connection points if this is a connection
            if (ann.type === "connection") {
              const boxX = Math.min(updatedAnn.start.x, updatedAnn.end.x);
              const boxY = Math.min(updatedAnn.start.y, updatedAnn.end.y);
              const boxWidth = Math.abs(updatedAnn.end.x - updatedAnn.start.x);
              const boxHeight = Math.abs(updatedAnn.end.y - updatedAnn.start.y);
              // Connection point at the inner rectangle edge (inner rectangle is inset by 8px)
              const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
              const tagHeight = RFI_TAG_HEIGHT; // Tag height
              const newConnectionCenter = {
                x: boxX + boxWidth - innerRectInset, // Inner right edge
                y: boxY + boxHeight - innerRectInset, // Inner bottom edge
              };
              // Tag position: tag should be positioned so its bottom edge aligns with inner rect bottom
              const newTagPosition = {
                x: newConnectionCenter.x,
                y: boxY + boxHeight - innerRectInset - tagHeight / 2, // Center tag vertically at inner rect bottom
              };
              setRfiTags((prev) =>
                prev.map((tag) =>
                  tag.connectionId === ann.id
                    ? {
                        ...tag,
                        connectionPoint: newConnectionCenter,
                        tagPosition: newTagPosition, // Update tag position so bottom edge aligns with box bottom
                      }
                    : tag,
                ),
              );
            }
            return updatedAnn;
          } else if (ann.type === "pen") {
            const match = ann.path.match(/M (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)/);
            if (match) {
              const originalX = parseFloat(match[1]);
              const originalY = parseFloat(match[2]);
              const deltaX = pt.x - dragOffset.x - originalX;
              const deltaY = pt.y - dragOffset.y - originalY;
              const path = ann.path.replace(
                /([ML]) (\d+(?:\.\d+)?) (\d+(?:\.\d+)?)/g,
                (match, cmd, x, y) => {
                  return `${cmd} ${parseFloat(x) + deltaX} ${
                    parseFloat(y) + deltaY
                  }`;
                },
              );
              return { ...ann, path };
            }
            return ann;
          }
          return ann;
        }),
      );
      return;
    }
    if (isPanning && pdfContainerRef.current) {
      const clientX = e.clientX || (e.touches && e.touches[0].clientX);
      const clientY = e.clientY || (e.touches && e.touches[0].clientY);
      const dx = clientX - panStart.x;
      const dy = clientY - panStart.y;
      pdfContainerRef.current.scrollLeft = panStart.scrollLeft - dx;
      pdfContainerRef.current.scrollTop = panStart.scrollTop - dy;
      return;
    }
    if (!isDrawing) return;
    const pt = getMousePos(e);
    if (isMovingConnection && connectionBoxStart && connectionBoxEnd) {
      const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
      const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
      const newStart = {
        x: pt.x - connectionMoveOffset.x,
        y: pt.y - connectionMoveOffset.y,
      };
      const newEnd = {
        x: newStart.x + boxWidth,
        y: newStart.y + boxHeight,
      };
      setConnectionBoxStart(newStart);
      setConnectionBoxEnd(newEnd);
      // Update RFI tags' connection points (rectangle bottom right corner)
      const boxX = Math.min(newStart.x, newEnd.x);
      const boxY = Math.min(newStart.y, newEnd.y);
      // Connection point at the inner rectangle edge (inner rectangle is inset by 8px)
      const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
      const tagHeight = RFI_TAG_HEIGHT; // Tag height
      const newConnectionCenter = {
        x: boxX + boxWidth - innerRectInset, // Inner right edge
        y: boxY + boxHeight - innerRectInset, // Inner bottom edge
      };
      // Tag position: tag should be positioned so its bottom edge aligns with inner rect bottom
      const newTagPosition = {
        x: newConnectionCenter.x,
        y: boxY + boxHeight - innerRectInset - tagHeight / 2, // Center tag vertically at inner rect bottom
      };
      setRfiTags((prev) =>
        prev.map((tag) => {
          // Update tags that are linked to this connection (by ID or by position if not yet saved)
          if (
            selectedAnnotationIndex !== null &&
            annotations[selectedAnnotationIndex]?.type === "connection"
          ) {
            const connectionId = annotations[selectedAnnotationIndex].id;
            if (tag.connectionId === connectionId) {
              return {
                ...tag,
                connectionPoint: newConnectionCenter,
                tagPosition: newTagPosition, // Update tag position so bottom edge aligns with box bottom
              };
            }
          }
          // Also update tags that don't have connectionId yet but are on the same page
          if (!tag.connectionId && tag.page === pageIndex) {
            // Check if tag's connection point is close to the old connection center (rectangle right edge)
            const oldBoxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
            const oldBoxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
            const oldBoxWidth = Math.abs(
              connectionBoxEnd.x - connectionBoxStart.x,
            );
            const oldBoxHeight = Math.abs(
              connectionBoxEnd.y - connectionBoxStart.y,
            );
            // Connection point at the inner edge of the border
            const oldStrokeWidth = 2;
            const oldConnectionCenter = {
              x: oldBoxX + oldBoxWidth - oldStrokeWidth, // Inner right edge (inside border)
              y: oldBoxY + oldBoxHeight / 2, // Center of rectangle height
            };
            const distance = Math.sqrt(
              Math.pow(tag.connectionPoint.x - oldConnectionCenter.x, 2) +
                Math.pow(tag.connectionPoint.y - oldConnectionCenter.y, 2),
            );
            if (distance < 50) {
              return {
                ...tag,
                connectionPoint: newConnectionCenter,
                tagPosition: newTagPosition, // Update tag position so bottom edge aligns with box bottom
              };
            }
          }
          return tag;
        }),
      );
      return;
    }
    if (
      isResizingConnection &&
      connectionBoxStart &&
      connectionBoxEnd &&
      resizeHandle
    ) {
      const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
      const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
      const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
      const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);

      if (resizeHandle === "nw") {
        setConnectionBoxStart({ x: pt.x, y: pt.y });
        setConnectionBoxEnd({ x: boxX + boxWidth, y: boxY + boxHeight });
      } else if (resizeHandle === "ne") {
        setConnectionBoxStart({ x: boxX, y: pt.y });
        setConnectionBoxEnd({ x: pt.x, y: boxY + boxHeight });
      } else if (resizeHandle === "sw") {
        setConnectionBoxStart({ x: pt.x, y: boxY });
        setConnectionBoxEnd({ x: boxX + boxWidth, y: pt.y });
      } else if (resizeHandle === "se") {
        setConnectionBoxEnd({ x: pt.x, y: pt.y });
        setConnectionBoxStart({ x: boxX, y: boxY });
      }
      return;
    }
    if (currentTool === TOOLS.CONNECTION && startPoint) {
      // Update connection box end point while drawing
      setConnectionBoxEnd(pt);
      return;
    }
    if (currentTool === TOOLS.CALLOUT && startPoint) {
      setCalloutDragEnd(pt);
      return;
    }
    if (currentTool === TOOLS.PEN) {
      setCurrentPath((prev) => `${prev} L ${pt.x} ${pt.y}`);
    } else if (currentTool === TOOLS.SHAPE && startPoint) {
      setCurrentPath({ start: startPoint, end: pt });
    }
  };

  const endDrawing = (e) => {
    if (draggingRfiTag !== null) {
      setDraggingRfiTag(null);
      setDragOffset({ x: 0, y: 0 });
      return;
    }
    if (draggingIndex !== null) {
      setDraggingIndex(null);
      draggingCalloutPartRef.current = null;
      calloutResizeStartRef.current = null;
      return;
    }
    if (isResizingConnection) {
      setIsResizingConnection(false);
      setResizeHandle(null);
      return;
    }
    if (isMovingConnection) {
      setIsMovingConnection(false);
      setConnectionMoveOffset({ x: 0, y: 0 });
      return;
    }
    if (isPanning) {
      setIsPanning(false);
      return;
    }
    if (!isDrawing) return;
    const endPoint = getMousePos(e);
    if (
      currentTool === TOOLS.PEN &&
      typeof currentPath === "string" &&
      currentPath
    ) {
      addAnnotation({
        type: "pen",
        path: currentPath,
        color,
        strokeWidth,
        page: pageIndex,
      });
      setCurrentPath("");
    } else if (currentTool === TOOLS.SHAPE && startPoint) {
      if (currentShape === SHAPES.CLOUD && cloudDrawingActive) {
        addAnnotation({
          type: "shape",
          shapeType: SHAPES.CLOUD,
          start: startPoint,
          end: endPoint,
          color,
          strokeWidth,
          page: pageIndex,
          cloudGroupId: activeCloudGroupId,
        });
        setIsDrawing(false);
        setTimeout(() => {
          setIsDrawing(true);
          setStartPoint(endPoint);
          setCurrentPath({ start: endPoint, end: endPoint });
        }, 0);
        return;
      } else {
        addAnnotation({
          type: "shape",
          shapeType: currentShape,
          start: startPoint,
          end: endPoint,
          color,
          strokeWidth,
          page: pageIndex,
        });
        setCurrentPath("");
      }
    } else if (currentTool === TOOLS.HIGHLIGHT && startPoint) {
      addAnnotation({
        type: "highlight",
        start: startPoint,
        end: endPoint,
        color: "#ffe066",
        page: pageIndex,
      });
      setCurrentPath("");
    } else if (currentTool === TOOLS.SQUIGGLY && startPoint) {
      addAnnotation({
        type: "squiggly",
        start: startPoint,
        end: endPoint,
        color: "red",
        page: pageIndex,
      });
      setCurrentPath("");
    } else if (currentTool === TOOLS.CONNECTION && startPoint) {
      setConnectionBoxStart(startPoint);
      setConnectionBoxEnd(endPoint);
      setShowConnectionBox(true);
      setConnectionText("");
      setIsDrawing(false);
      setStartPoint(null);
      return;
    } else if (currentTool === TOOLS.CALLOUT && startPoint) {
      setIsDrawing(false);
      setStartPoint(null);
      setCalloutDragEnd(null);
      openNewTextEditor(
        getCalloutBoxCenter(startPoint, endPoint, TEXT_ANNOTATION_DEFAULT_WIDTH),
        startPoint,
      );
      return;
    }
    setIsDrawing(false);
    setStartPoint(null);
    if (
      currentTool === TOOLS.SHAPE &&
      currentShape === SHAPES.CLOUD &&
      cloudDrawingActive
    ) {
    } else {
      setCloudDrawingActive(false);
      setCloudStartPoint(null);
    }
  };

  const mutationUpdate = useMutation({
    mutationFn: (data) => updateDrawing(projectId, drawing._id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["drawings", projectId]);
    },
    onError: (error) => {
      notification.error({
        message: "Failed to update drawing",
        description: error.message,
      });
    },
  });

  const addTextAnnotation = (value) => {
    const text = value !== undefined ? value : textInput;
    const position = textInputPosRef.current || textInputPos;
    const editingIdx = selectedAnnotationIndex;
    const isEditingText =
      editingIdx !== null && annotations[editingIdx]?.type === "text";
    const tip = calloutTipRef.current;
    const calloutFields = tip
      ? { calloutTip: tip, calloutKnee: calloutKneeRef.current || undefined }
      : {};

    const closeTextEditor = () => {
      textInputPosRef.current = null;
      setTextInput("");
      textInputRef.current = "";
      setShowTextInput(false);
      showTextInputRef.current = false;
      setTextInputPos(null);
      setSelectedAnnotationIndex(null);
      manualTextHeightRef.current = null;
      calloutTipRef.current = null;
      setCalloutTip(null);
      calloutKneeRef.current = null;
      setCalloutKnee(null);
      // Stay on TEXT/CALLOUT so font/color controls remain available to restyle or place another.
      setCurrentTool(tip ? TOOLS.CALLOUT : TOOLS.TEXT);
    };

    // Empty commit: remove the annotation being edited so we don't leave an invisible orphan.
    // New drafts with no text simply close without creating anything.
    // Consultants cannot delete saved annotations by clearing the editor.
    if (!text?.trim()) {
      if (isEditingText) {
        const existing = annotations[editingIdx];
        const canDelete =
          canDeletePersistedAnnotations ||
          !existing?.id ||
          !persistedAnnotationIds.has(existing.id);
        if (canDelete) {
          setAnnotations((prev) => prev.filter((_, i) => i !== editingIdx));
        } else {
          const saved = lastSavedAnnotations.find((a) => a.id === existing.id);
          if (saved) {
            setAnnotations((prev) =>
              prev.map((ann, i) =>
                i === editingIdx ? { ...ann, content: saved.content } : ann,
              ),
            );
          }
        }
      }
      closeTextEditor();
      return;
    }
    // Guard against double-commit: blur and outside-click both fire on finish.
    if (!position) return;
    textInputPosRef.current = null;
    const style = {
      fontWeight: textBold ? "bold" : "normal",
      fontStyle: textItalic ? "italic" : "normal",
      textDecoration: `${textUnderline ? "underline " : ""}${
        textStrike ? "line-through" : ""
      }`.trim(),
    };
    const boxWidth = inputWidth || TEXT_ANNOTATION_DEFAULT_WIDTH;
    const contentHeight = getTextAnnotationMetrics(
      text,
      fontSize,
      boxWidth,
    ).height;
    const naturalHeight = Math.max(fontSize * 1.5, contentHeight + 8);
    const boxHeight =
      inputHeight > naturalHeight + 0.5 ? inputHeight : undefined;
    if (isEditingText) {
      setAnnotations((prev) =>
        prev.map((ann, i) =>
          i === editingIdx
            ? {
                ...ann,
                content: text,
                position,
                fontSize,
                color,
                boxWidth,
                boxHeight,
                ...calloutFields,
                style: {
                  ...style,
                  highlight: textHighlight,
                  squiggly: textSquiggly,
                },
              }
            : ann,
        ),
      );
    } else {
      addAnnotation({
        type: "text",
        content: text,
        position,
        color,
        fontSize,
        boxWidth,
        boxHeight,
        ...calloutFields,
        page: pageIndex,
        style: {
          ...style,
          highlight: textHighlight,
          squiggly: textSquiggly,
        },
      });
    }
    closeTextEditor();
  };

  const getEditorCalloutBox = () => {
    if (!textInputPos) return null;
    const height = Math.max(inputHeight, fontSize * 1.5);
    return {
      left: textInputPos.x - inputWidth / 2,
      top: textInputPos.y - height / 2,
      width: inputWidth,
      height,
    };
  };

  const setEditorCalloutPoint = (part, point) => {
    const editingIdx = selectedAnnotationIndex;
    const field = part === "knee" ? "calloutKnee" : "calloutTip";
    if (part === "knee") {
      calloutKneeRef.current = point;
      setCalloutKnee(point);
    } else {
      calloutTipRef.current = point;
      setCalloutTip(point);
    }
    if (editingIdx !== null) {
      setAnnotations((prev) =>
        prev.map((ann, i) =>
          i === editingIdx ? { ...ann, [field]: point || undefined } : ann,
        ),
      );
    }
  };

  const handleEditorCalloutHandlePointerDown = (part) => (e) => {
    if (!calloutTipRef.current) return;
    const origin =
      part === "knee"
        ? getCalloutLeaderGeometry(
            calloutTipRef.current,
            getEditorCalloutBox(),
            calloutKneeRef.current,
          )?.points[1]
        : calloutTipRef.current;
    if (!origin) return;
    e.preventDefault();
    e.stopPropagation();
    isResizingTextRef.current = true;
    const start = getMousePos(e);
    const onMove = (ev) => {
      const pt = getMousePos(ev);
      setEditorCalloutPoint(part, {
        x: origin.x + pt.x - start.x,
        y: origin.y + pt.y - start.y,
      });
    };
    const onUp = () => {
      isResizingTextRef.current = false;
      inputRef.current?.focus?.();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  };

  // Connection Box handlers
  const handleConnectionMoveStart = (pt) => {
    const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
    const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
    setIsMovingConnection(true);
    setIsDrawing(true);
    setConnectionMoveOffset({
      x: pt.x - boxX,
      y: pt.y - boxY,
    });
  };

  const handleConnectionResizeStart = (handle) => {
    setIsResizingConnection(true);
    setResizeHandle(handle);
    setIsDrawing(true);
  };

  const handleConnectionPublish = () => {
    if (connectionBoxStart && connectionBoxEnd) {
      if (
        selectedAnnotationIndex !== null &&
        annotations[selectedAnnotationIndex]?.type === "connection"
      ) {
        // Update existing connection annotation (preserve rfiTag if exists)
        const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
        const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
        const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
        const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
        const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
        const tagHeight = RFI_TAG_HEIGHT;
        const newConnectionCenter = {
          x: boxX + boxWidth - innerRectInset,
          y: boxY + boxHeight - innerRectInset,
        };
        const newTagPosition = {
          x: newConnectionCenter.x,
          y: boxY + boxHeight - innerRectInset - tagHeight / 2,
        };

        let existingRfiTag = null;
        setAnnotations((prev) => {
          const prevAnn = prev[selectedAnnotationIndex];
          const hasPhotoLink =
            !!prevAnn?.linkedPhotoUrl ||
            !!prevAnn?.photoTag ||
            annotationHasLinkedPhoto(prevAnn);
          existingRfiTag = hasPhotoLink ? null : prevAnn?.rfiTag;

          return prev.map((ann, i) =>
            i === selectedAnnotationIndex
              ? {
                  ...ann,
                  start: connectionBoxStart,
                  end: connectionBoxEnd,
                  content: "",
                  rfiTag: hasPhotoLink
                    ? null
                    : prevAnn.rfiTag
                      ? {
                          ...prevAnn.rfiTag,
                          connectionPoint: newConnectionCenter,
                          tagPosition: newTagPosition,
                        }
                      : ann.rfiTag,
                  linkedRfiId: hasPhotoLink
                    ? null
                    : prevAnn.linkedRfiId || ann.linkedRfiId,
                  linkedRfiTagId: hasPhotoLink
                    ? null
                    : prevAnn.linkedRfiTagId || ann.linkedRfiTagId,
                  photoTag: prevAnn.photoTag
                    ? {
                        ...prevAnn.photoTag,
                        connectionPoint: newConnectionCenter,
                        tagPosition: newTagPosition,
                      }
                    : ann.photoTag,
                  linkedPhotoUrl: prevAnn.linkedPhotoUrl ?? ann.linkedPhotoUrl,
                  linkedPhotoName:
                    prevAnn.linkedPhotoName ?? ann.linkedPhotoName,
                  linkedPhotoPreviewUrl:
                    prevAnn.linkedPhotoPreviewUrl ?? ann.linkedPhotoPreviewUrl,
                  linkedPhoto:
                    prevAnn.linkedPhotoUrl || ann.linkedPhotoUrl
                      ? undefined
                      : prevAnn.linkedPhotoPreviewUrl ||
                          ann.linkedPhotoPreviewUrl
                        ? undefined
                        : (prevAnn.linkedPhoto ?? ann.linkedPhoto),
                  linkedPhotoTagId:
                    prevAnn.linkedPhotoTagId ?? ann.linkedPhotoTagId,
                }
              : ann,
          );
        });

        if (existingRfiTag) {
          setRfiTags((prev) =>
            prev.map((tag) =>
              tag.id === existingRfiTag.id
                ? {
                    ...tag,
                    connectionPoint: newConnectionCenter,
                    tagPosition: newTagPosition,
                  }
                : tag,
            ),
          );
        }

        setSelectedAnnotationIndex(null);
      } else {
        // Create new connection annotation
        const newConnection = addAnnotation({
          type: "connection",
          start: connectionBoxStart,
          end: connectionBoxEnd,
          content: "",
          page: pageIndex,
        });
        // Update RFI tags that were created for this connection and add rfiTag to annotation
        let rfiTagToAdd = null;
        setRfiTags((prev) =>
          prev.map((tag) => {
            if (!tag.connectionId && tag.page === pageIndex) {
              const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
              const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
              const boxWidth = Math.abs(
                connectionBoxEnd.x - connectionBoxStart.x,
              );
              const boxHeight = Math.abs(
                connectionBoxEnd.y - connectionBoxStart.y,
              );
              const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
              const tagHeight = RFI_TAG_HEIGHT;
              const connectionCenter = {
                x: boxX + boxWidth - innerRectInset,
                y: boxY + boxHeight - innerRectInset,
              };
              const tagPosition = {
                x: connectionCenter.x,
                y: boxY + boxHeight - innerRectInset - tagHeight / 2,
              };
              const distance = Math.sqrt(
                Math.pow(tag.connectionPoint.x - connectionCenter.x, 2) +
                  Math.pow(tag.connectionPoint.y - connectionCenter.y, 2),
              );
              if (distance < 50) {
                const updatedTag = {
                  ...tag,
                  connectionId: newConnection.id,
                  connectionPoint: connectionCenter,
                  tagPosition: tagPosition,
                };
                rfiTagToAdd = updatedTag;
                return updatedTag;
              }
            }
            return tag;
          }),
        );

        if (rfiTagToAdd) {
          setAnnotations((prevAnn) => {
            const existingAnn = prevAnn.find(
              (ann) => ann.id === newConnection.id,
            );
            if (existingAnn) {
              return prevAnn.map((ann) =>
                ann.id === newConnection.id
                  ? {
                      ...ann,
                      linkedRfiId: rfiTagToAdd.rfiId,
                      linkedRfiTagId: rfiTagToAdd.id,
                      rfiTag: rfiTagToAdd,
                    }
                  : ann,
              );
            }
            return prevAnn;
          });
        }
        if (pendingPhotoRef) {
          const pf = buildPhotoLinkFields(
            connectionBoxStart,
            connectionBoxEnd,
            pageIndex,
            newConnection.id,
            pendingPhotoRef,
          );
          setAnnotations((prevAnn) =>
            prevAnn.map((ann) =>
              ann.id === newConnection.id
                ? {
                    ...ann,
                    ...pf,
                    linkedRfiId: null,
                    linkedRfiTagId: null,
                    rfiTag: null,
                  }
                : ann,
            ),
          );
          setRfiTags((prev) =>
            prev.filter((t) => t.connectionId !== newConnection.id),
          );
          if (pendingPhotoRef.file instanceof File) {
            connectionPhotoFilesRef.current.set(
              newConnection.id,
              pendingPhotoRef.file,
            );
          }
          setPendingPhotoRef(null);
        }
        setSelectedAnnotationIndex(null);
      }
      // Clear connection box state after annotation is saved
      setShowConnectionBox(false);
      setConnectionBoxStart(null);
      setConnectionBoxEnd(null);
      setConnectionText("");
      setCurrentTool(TOOLS.PAN);
      setPendingPhotoRef(null);
    }
  };

  const handleCreatePunchListItemClose = () => {
    setShowCreatePunchListItemModal(false);
    queryClient.invalidateQueries(["punch-lists-drawing-link", projectId]);
    if (linkModalPunchListId) {
      queryClient.invalidateQueries([
        "punch-list-items-drawing-link",
        linkModalPunchListId,
      ]);
    }
  };

  const handlePunchListItemCreated = async (createdItem) => {
    const createdItemId = createdItem?.id ?? createdItem?._id;
    if (!createdItemId) {
      return;
    }
    // drawings_id is synced on drawing save after annotations persist successfully.
    setCreatedPunchListItemForLink(createdItem);
    setSelectedRfiId(createdItemId);
    setIsPunchListItemSelectOpen(false);
  };

  const handleOpenCreatePunchListItem = () => {
    if (!linkModalPunchListId) {
      message.warning("Select a punch list first");
      return;
    }
    setIsPunchListFolderSelectOpen(false);
    setIsPunchListItemSelectOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTimeout(() => setShowCreatePunchListItemModal(true), 0);
  };

  const handleConnectionLink = (type = "rfi") => {
    if (type === "photo") {
      photoLinkInputRef.current?.click();
      return;
    }
    if (type === "drawing") {
      const current =
        selectedAnnotationIndex !== null
          ? annotations[selectedAnnotationIndex]
          : null;
      setDrawingSearch(
        isDrawingLink(current) ? current.targetDrawingNumber || "" : "",
      );
      setSelectedRfiId(
        isDrawingLink(current) ? current.targetDrawingId || null : null,
      );
    }
    setLinkType(type);
    setShowLinkModal(true);
  };

  const handleConfirmDrawingLink = () => {
    if (!selectedRfiId) {
      message.warning("Please select a drawing");
      return;
    }
    const selected = drawingsForLink.find(
      (item) => String(item.id ?? item._id) === String(selectedRfiId),
    );
    if (!selected) {
      message.warning("Please select a drawing");
      return;
    }

    const existingLink =
      selectedAnnotationIndex !== null
        ? annotations[selectedAnnotationIndex]
        : null;
    if (isAutoGeneratedDrawingLink(existingLink)) {
      setAnnotations((prev) =>
        prev.map((ann) =>
          ann.id === existingLink.id
            ? {
                ...ann,
                targetDrawingNumber: selected.drawing_number || "",
                targetDrawingCode: null,
                targetDrawingTitle: selected.title || null,
                targetDrawingId: selected._id ?? selected.id,
                targetFolderId: selected.drawing_folder_id,
                autoGenerated: true,
              }
            : ann,
        ),
      );
      setSelectedAnnotationIndex(null);
      setShowLinkModal(false);
      setSelectedRfiId(null);
      setDrawingSearch("");
      setLinkType(null);
      return;
    }

    let start = connectionBoxStart;
    let end = connectionBoxEnd;
    if (
      (!start || !end) &&
      selectedAnnotationIndex !== null &&
      annotations[selectedAnnotationIndex]?.type === "connection"
    ) {
      const ann = annotations[selectedAnnotationIndex];
      start = ann.start;
      end = ann.end;
    }
    if (!start || !end) {
      message.warning("Draw a connection box around the callout first");
      return;
    }

    if (pendingPhotoRef?.objectUrl?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(pendingPhotoRef.objectUrl);
      } catch (e) {}
    }
    setPendingPhotoRef(null);

    const drawingConnection = {
      type: "connection",
      start,
      end,
      page: pageIndex,
      targetDrawingNumber: selected.drawing_number || "",
      targetDrawingCode: null,
      targetDrawingId: selected._id ?? selected.id,
      targetFolderId: selected.drawing_folder_id,
      calloutKind: "detail",
      autoGenerated: false,
      linkedRfiId: null,
      linkedRfiTagId: null,
      rfiTag: null,
      linkedPhoto: null,
      linkedPhotoUrl: null,
      linkedPhotoName: null,
      linkedPhotoPreviewUrl: null,
      linkedPhotoTagId: null,
      photoTag: null,
    };

    if (
      selectedAnnotationIndex !== null &&
      annotations[selectedAnnotationIndex]?.type === "connection"
    ) {
      const existing = annotations[selectedAnnotationIndex];
      if (existing.linkedPhotoPreviewUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(existing.linkedPhotoPreviewUrl);
        } catch (e) {}
      }
      connectionPhotoFilesRef.current.delete(existing.id);
      if (existing.type === "connection" && (existing.linkedRfiTagId || existing.id)) {
        setRfiTags((prev) =>
          prev.filter(
            (tag) =>
              tag.id !== existing.linkedRfiTagId &&
              tag.connectionId !== existing.id,
          ),
        );
      }
      const replaceIndex = selectedAnnotationIndex;
      setAnnotations((prev) =>
        prev.map((ann, i) =>
          i === replaceIndex
            ? {
                ...ann,
                ...drawingConnection,
                id: ann.id,
                user: ann.user,
                calloutKind: ann.calloutKind || drawingConnection.calloutKind,
                autoGenerated: false,
              }
            : ann,
        ),
      );
      setSelectedAnnotationIndex(null);
    } else {
      addAnnotation(drawingConnection);
    }

    setShowConnectionBox(false);
    setConnectionBoxStart(null);
    setConnectionBoxEnd(null);
    setConnectionText("");
    setCurrentTool(TOOLS.PAN);
    setShowLinkModal(false);
    setSelectedRfiId(null);
    setDrawingSearch("");
    setLinkType(null);
  };

  const handleConnectionDelete = () => {
    if (!canDeleteConnectionBox) return;
    // Clean up RFI tags associated with the connection being deleted
    if (
      selectedAnnotationIndex !== null &&
      annotations[selectedAnnotationIndex]?.type === "connection"
    ) {
      const annotationToDelete = annotations[selectedAnnotationIndex];
      if (annotationToDelete.linkedPhotoPreviewUrl?.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(annotationToDelete.linkedPhotoPreviewUrl);
        } catch (e) {}
      }
      connectionPhotoFilesRef.current.delete(annotationToDelete.id);

      // Remove RFI tag linked by linkedRfiTagId
      if (annotationToDelete.linkedRfiTagId) {
        setRfiTags((prev) =>
          prev.filter((tag) => tag.id !== annotationToDelete.linkedRfiTagId),
        );
      }

      // Remove RFI tags linked by connectionId
      setRfiTags((prev) =>
        prev.filter((tag) => tag.connectionId !== annotationToDelete.id),
      );

      // Remove the annotation
      setAnnotations((prev) =>
        prev.filter((_, i) => i !== selectedAnnotationIndex),
      );
      setSelectedAnnotationIndex(null);
    }

    // Also clean up any pending RFI tags (tags without connectionId) in the current connection box area
    if (connectionBoxStart && connectionBoxEnd) {
      const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
      const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
      const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
      const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
      const innerRectInset = CONNECTION_INNER_RECT_INSET;
      const connectionCenter = {
        x: boxX + boxWidth - innerRectInset,
        y: boxY + boxHeight - innerRectInset,
      };

      setRfiTags((prev) =>
        prev.filter((tag) => {
          // Keep tags that have a connectionId (belong to other connections)
          if (tag.connectionId) return true;
          // Keep tags on different pages
          if (tag.page !== pageIndex) return true;
          // Remove pending tags near the current connection box
          const distance = Math.sqrt(
            Math.pow(tag.connectionPoint.x - connectionCenter.x, 2) +
              Math.pow(tag.connectionPoint.y - connectionCenter.y, 2),
          );
          return distance >= 50; // Keep if distance is >= 50 (not in proximity)
        }),
      );
    }

    setShowConnectionBox(false);
    setConnectionBoxStart(null);
    setConnectionBoxEnd(null);
    setConnectionText("");
    setCurrentTool(TOOLS.PAN);
    setPendingPhotoRef(null);
  };

  const buildCloudSegmentAnnotation = (segment, groupId) => ({
    type: "shape",
    shapeType: SHAPES.CLOUD,
    start: segment.start,
    end: segment.end,
    color,
    strokeWidth,
    page: pageIndex,
    cloudGroupId: groupId,
  });

  const finalizeCloudDrawing = (finalSegment = null) => {
    const groupId = activeCloudGroupId;
    if (!groupId) {
      setCloudDrawingActive(false);
      setCloudStartPoint(null);
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentPath("");
      return;
    }

    const userName = currentUser
      ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() ||
        currentUser.email ||
        currentUser.username ||
        "Unknown User"
      : "Unknown User";

    setAnnotations((prev) => {
      let updated = [...prev];
      if (finalSegment) {
        updated.push({
          ...buildCloudSegmentAnnotation(finalSegment, groupId),
          id: uuidv4(),
          user: currentUser
            ? {
                id: currentUser.id || currentUser._id,
                name: userName,
                email: currentUser.email,
              }
            : undefined,
        });
      }
      return mergeCloudGroupAnnotations(updated, groupId);
    });

    setActiveCloudGroupId(null);
    setCloudDrawingActive(false);
    setCloudStartPoint(null);
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentPath("");
  };

  const addAnnotation = (annotation) => {
    const userName = currentUser
      ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() ||
        currentUser.email ||
        currentUser.username ||
        "Unknown User"
      : "Unknown User";

    const newAnnotation = {
      ...annotation,
      id: annotation.id || uuidv4(),
      user: currentUser
        ? {
            id: currentUser.id || currentUser._id,
            name: userName,
            email: currentUser.email,
          }
        : undefined,
    };

    setAnnotations((prev) => [...prev, newAnnotation]);

    // If this is a connection annotation, update RFI tags that don't have a connectionId yet
    if (annotation.type === "connection") {
      setRfiTags((prev) =>
        prev.map((tag) => {
          // If tag doesn't have connectionId but matches this connection's position
          if (!tag.connectionId && tag.page === pageIndex) {
            const boxX = Math.min(annotation.start.x, annotation.end.x);
            const boxY = Math.min(annotation.start.y, annotation.end.y);
            const boxWidth = Math.abs(annotation.end.x - annotation.start.x);
            const boxHeight = Math.abs(annotation.end.y - annotation.start.y);
            // Connection point at the inner rectangle edge (inner rectangle is inset by 8px)
            const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
            const connectionCenter = {
              x: boxX + boxWidth - innerRectInset, // Inner right edge
              y: boxY + boxHeight - innerRectInset, // Inner bottom edge
            };
            // Check if tag's connection point is close to this connection
            const distance = Math.sqrt(
              Math.pow(tag.connectionPoint.x - connectionCenter.x, 2) +
                Math.pow(tag.connectionPoint.y - connectionCenter.y, 2),
            );
            if (distance < 50) {
              return {
                ...tag,
                connectionId: newAnnotation.id,
                connectionPoint: connectionCenter,
              };
            }
          }
          return tag;
        }),
      );
    }

    return newAnnotation;
  };

  const openLinkedDrawing = (annotation) => {
    const targetId = annotation?.targetDrawingId;
    const number = annotation?.targetDrawingNumber || "Drawing";
    if (!targetId) {
      message.info(`${number} not found in this folder`);
      return;
    }
    if (String(targetId) === String(drawingId)) {
      return;
    }
    onSelectFolderDrawing?.({
      _id: targetId,
      drawing_folder_id: annotation.targetFolderId,
    });
  };

  const handleAnnotationClick = (index, e) => {
    const annotation = annotations[index];

    if (isAutoGeneratedDrawingLink(annotation)) {
      e.stopPropagation();
      setSelectedAnnotationIndex(null);
      setSheetLinkMenuId(annotation.id);
      return;
    }
    if (annotation?.type === "connection") {
      e.stopPropagation();
      setPendingPhotoRef(null);
      setSelectedAnnotationIndex(index);
      setConnectionBoxStart(annotation.start);
      setConnectionBoxEnd(annotation.end);
      setConnectionText(annotation.content || "");
      setShowConnectionBox(true);
      setCurrentTool(TOOLS.CONNECTION);
      return;
    }
    e.stopPropagation();
    setSelectedAnnotationIndex(index);
  };

  const handleAnnotationDoubleClick = (index, e) => {
    e.stopPropagation();
    const ann = annotations[index];
    if (ann.type === "text") {
      beginEditingTextAnnotation(ann, index, e);
    }
  };

  const isPersistedAnnotation = (ann) =>
    !!(ann?.id && persistedAnnotationIds.has(ann.id));

  // A saved member must not lock the user's own unsaved marks in the same cloud group.
  const getDeletableIndicesAt = (index) => {
    if (index === null || index === undefined || !annotations[index]) {
      return [];
    }
    const indices = findConnectedCloudAnnotationIndices(annotations, index);
    if (canDeletePersistedAnnotations) return indices;
    if (isPersistedAnnotation(annotations[index])) return [];
    return indices.filter((i) => !isPersistedAnnotation(annotations[i]));
  };

  const canDeleteAnnotationAt = (index) =>
    getDeletableIndicesAt(index).length > 0;

  const canDeleteSelectedAnnotation = canDeleteAnnotationAt(
    selectedAnnotationIndex,
  );
  const canDeleteConnectionBox =
    selectedAnnotationIndex === null || canDeleteSelectedAnnotation;
  const hasNewAnnotations = annotations.some(
    (ann) => !ann?.id || !persistedAnnotationIds.has(ann.id),
  );
  const canClearAnnotations =
    canDeletePersistedAnnotations || hasNewAnnotations;

  const sheetLinkMenuIndex = sheetLinkMenuId
    ? annotations.findIndex(
        (ann) =>
          ann.id === sheetLinkMenuId &&
          ann.page === pageIndex &&
          isAutoGeneratedDrawingLink(ann),
      )
    : -1;
  const sheetLinkMenuAnnotation =
    sheetLinkMenuIndex !== -1 ? annotations[sheetLinkMenuIndex] : null;
  const sheetLinkMenuAnchor = (() => {
    if (!sheetLinkMenuAnnotation) return null;
    const { start, end } = sheetLinkMenuAnnotation;
    const radius =
      getSheetLinkHitRadius(sheetLinkMenuAnnotation, visualZoom) * visualZoom;
    return {
      left: ((start.x + end.x) / 2) * visualZoom - radius,
      top: ((start.y + end.y) / 2) * visualZoom - radius,
      size: radius * 2,
    };
  })();

  const handleSheetLinkGoTo = () => {
    const annotation = sheetLinkMenuAnnotation;
    setSheetLinkMenuId(null);
    goToLatestLinkedDrawing(annotation);
  };

  const goToLatestLinkedDrawing = async (annotation) => {
    const number = annotation?.targetDrawingCode || annotation?.targetDrawingNumber;
    const folderId = annotation?.targetFolderId || drawingFolderId;
    if (number && folderId && projectId) {
      try {
        const result = await fetchLatestDrawingRevision(projectId, number, folderId);
        const latest = result?.drawing;
        if (latest?._id) {
          if (String(latest._id) === String(drawingId)) return;
          onSelectFolderDrawing?.({
            _id: latest._id,
            drawing_folder_id: latest.drawing_folder_id || folderId,
          });
          return;
        }
      } catch (error) {
        console.error("Error finding the latest revision:", error);
      }
    }
    openLinkedDrawing(annotation);
  };

  const handleChangeSheetLinkTarget = () => {
    const annotation = sheetLinkMenuAnnotation;
    setSheetLinkMenuId(null);
    if (!annotation) return;
    setSelectedAnnotationIndex(sheetLinkMenuIndex);
    setDrawingSearch(annotation.targetDrawingNumber || "");
    setSelectedRfiId(annotation.targetDrawingId || null);
    setLinkType("drawing");
    setShowLinkModal(true);
  };

  const handleDeleteSheetLink = () => {
    const annotation = sheetLinkMenuAnnotation;
    setSheetLinkMenuId(null);
    if (!annotation) return;
    if (!canDeleteAnnotationAt(sheetLinkMenuIndex)) {
      message.warning("Consultants cannot delete saved annotations.");
      return;
    }
    Modal.confirm({
      title: "Delete Sheet Link",
      content: `Are you sure you want to delete the link to ${
        annotation.targetDrawingCode || annotation.targetDrawingNumber || "this sheet"
      }?`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        setAnnotations((prev) => prev.filter((ann) => ann.id !== annotation.id));
        setSelectedAnnotationIndex(null);
      },
    });
  };

  const deleteSelectedAnnotation = () => {
    const indicesToDelete = getDeletableIndicesAt(selectedAnnotationIndex);
    if (indicesToDelete.length > 0) {
      const annotationToDelete = annotations[selectedAnnotationIndex];

      // If deleting a connection annotation, also delete its associated RFI tag
      if (annotationToDelete?.type === "connection") {
        if (annotationToDelete.linkedRfiTagId) {
          setRfiTags((prev) =>
            prev.filter((tag) => tag.id !== annotationToDelete.linkedRfiTagId),
          );
        }
        // Also check if any tags are linked to this connection by connectionId
        setRfiTags((prev) =>
          prev.filter((tag) => tag.connectionId !== annotationToDelete.id),
        );
      }

      if (showTextInputRef.current) {
        suppressTextBlurRef.current = true;
        setTimeout(() => {
          suppressTextBlurRef.current = false;
        }, 50);
        textInputPosRef.current = null;
        setTextInput("");
        textInputRef.current = "";
        setShowTextInput(false);
        showTextInputRef.current = false;
        setTextInputPos(null);
        manualTextHeightRef.current = null;
        calloutTipRef.current = null;
        setCalloutTip(null);
        calloutKneeRef.current = null;
        setCalloutKnee(null);
      }

      setAnnotations((prev) =>
        prev.filter((_, i) => !indicesToDelete.includes(i)),
      );
      setSelectedAnnotationIndex(null);
    }
  };

  const handleSave = async () => {
    if (detectingSheetLinks) {
      message.warning("Please wait until sheet link detection finishes");
      return;
    }
    if (savingAnnotations) return;
    setSavingAnnotations(true);
    try {
      await saveAnnotations();
    } finally {
      setSavingAnnotations(false);
    }
  };

  const saveAnnotations = async () => {
    if (drawing) {
      // If there's an active connection box being edited, publish it first before saving
      // We need to update annotations state and then use the updated state for saving
      let updatedAnnotations = [...annotations];

      if (showConnectionBox && connectionBoxStart && connectionBoxEnd) {
        if (
          selectedAnnotationIndex !== null &&
          annotations[selectedAnnotationIndex]?.type === "connection"
        ) {
          // Update existing connection annotation (preserve rfiTag if exists)
          const existingAnn = annotations[selectedAnnotationIndex];
          const hasPhotoLink =
            !!existingAnn.linkedPhotoUrl ||
            !!existingAnn.photoTag ||
            annotationHasLinkedPhoto(existingAnn);
          const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
          const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
          const boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
          const boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
          const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
          const tagHeight = RFI_TAG_HEIGHT; // Tag height
          const newConnectionCenter = {
            x: boxX + boxWidth - innerRectInset, // Inner right edge
            y: boxY + boxHeight - innerRectInset, // Inner bottom edge
          };
          // Tag position: tag should be positioned so its bottom edge aligns with inner rect bottom
          const newTagPosition = {
            x: newConnectionCenter.x,
            y: boxY + boxHeight - innerRectInset - tagHeight / 2, // Center tag vertically at inner rect bottom
          };

          // Update annotations array directly
          updatedAnnotations = updatedAnnotations.map((ann, i) =>
            i === selectedAnnotationIndex
              ? {
                  ...ann,
                  start: connectionBoxStart,
                  end: connectionBoxEnd,
                  content: "", // No textarea, just selection rectangle
                  rfiTag: hasPhotoLink
                    ? null
                    : existingAnn.rfiTag
                      ? {
                          ...existingAnn.rfiTag,
                          connectionPoint: newConnectionCenter,
                          tagPosition: newTagPosition,
                        }
                      : ann.rfiTag,
                  linkedRfiId: hasPhotoLink
                    ? null
                    : existingAnn.linkedRfiId || ann.linkedRfiId,
                  linkedRfiTagId: hasPhotoLink
                    ? null
                    : existingAnn.linkedRfiTagId || ann.linkedRfiTagId,
                  photoTag: existingAnn.photoTag
                    ? {
                        ...existingAnn.photoTag,
                        connectionPoint: newConnectionCenter,
                        tagPosition: newTagPosition,
                      }
                    : ann.photoTag,
                  linkedPhotoUrl:
                    existingAnn.linkedPhotoUrl ?? ann.linkedPhotoUrl,
                  linkedPhotoName:
                    existingAnn.linkedPhotoName ?? ann.linkedPhotoName,
                  linkedPhotoPreviewUrl:
                    existingAnn.linkedPhotoPreviewUrl ??
                    ann.linkedPhotoPreviewUrl,
                  linkedPhoto:
                    existingAnn.linkedPhotoUrl || ann.linkedPhotoUrl
                      ? undefined
                      : existingAnn.linkedPhotoPreviewUrl ||
                          ann.linkedPhotoPreviewUrl
                        ? undefined
                        : (existingAnn.linkedPhoto ?? ann.linkedPhoto),
                  linkedPhotoTagId:
                    existingAnn.linkedPhotoTagId ?? ann.linkedPhotoTagId,
                }
              : ann,
          );

          // Update rfiTags state with new connectionPoint and tagPosition
          if (!hasPhotoLink && existingAnn.rfiTag) {
            setRfiTags((prev) =>
              prev.map((tag) =>
                tag.id === existingAnn.rfiTag.id
                  ? {
                      ...tag,
                      connectionPoint: newConnectionCenter,
                      tagPosition: newTagPosition, // Update tag position so bottom edge aligns with box bottom
                    }
                  : tag,
              ),
            );
          }

          setSelectedAnnotationIndex(null);
        } else {
          // Create new connection annotation
          const userName = currentUser
            ? `${currentUser.first_name || ""} ${currentUser.last_name || ""}`.trim() ||
              currentUser.email ||
              currentUser.username ||
              "Unknown User"
            : "Unknown User";

          const newConnection = {
            type: "connection",
            start: connectionBoxStart,
            end: connectionBoxEnd,
            content: "", // No textarea, just selection rectangle
            page: pageIndex,
            id: uuidv4(),
            user: currentUser
              ? {
                  id: currentUser.id || currentUser._id,
                  name: userName,
                  email: currentUser.email,
                }
              : undefined,
          };

          // Update RFI tags that were created for this connection and add rfiTag to annotation
          let rfiTagToAdd = null;
          const updatedRfiTags = rfiTags.map((tag) => {
            if (!tag.connectionId && tag.page === pageIndex) {
              const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
              const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
              const boxWidth = Math.abs(
                connectionBoxEnd.x - connectionBoxStart.x,
              );
              const boxHeight = Math.abs(
                connectionBoxEnd.y - connectionBoxStart.y,
              );
              const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
              const tagHeight = RFI_TAG_HEIGHT; // Tag height
              const connectionCenter = {
                x: boxX + boxWidth - innerRectInset, // Inner right edge
                y: boxY + boxHeight - innerRectInset, // Inner bottom edge
              };
              // Tag position: tag should be positioned so its bottom edge aligns with inner rect bottom
              const tagPosition = {
                x: connectionCenter.x,
                y: boxY + boxHeight - innerRectInset - tagHeight / 2, // Center tag vertically at inner rect bottom
              };
              const distance = Math.sqrt(
                Math.pow(tag.connectionPoint.x - connectionCenter.x, 2) +
                  Math.pow(tag.connectionPoint.y - connectionCenter.y, 2),
              );
              if (distance < 50) {
                const updatedTag = {
                  ...tag,
                  connectionId: newConnection.id,
                  connectionPoint: connectionCenter,
                  tagPosition: tagPosition, // Update tag position so bottom edge aligns with box bottom
                };
                rfiTagToAdd = updatedTag;
                return updatedTag;
              }
            }
            return tag;
          });

          // Update annotation with rfiTag if found
          if (rfiTagToAdd) {
            newConnection.linkedRfiId = rfiTagToAdd.rfiId;
            newConnection.linkedRfiTagId = rfiTagToAdd.id;
            newConnection.rfiTag = rfiTagToAdd;
          }
          if (pendingPhotoRef) {
            Object.assign(
              newConnection,
              buildPhotoLinkFields(
                connectionBoxStart,
                connectionBoxEnd,
                pageIndex,
                newConnection.id,
                pendingPhotoRef,
              ),
              {
                linkedRfiId: null,
                linkedRfiTagId: null,
                rfiTag: null,
              },
            );
            if (pendingPhotoRef.file instanceof File) {
              connectionPhotoFilesRef.current.set(
                newConnection.id,
                pendingPhotoRef.file,
              );
            }
          }

          // Add new connection to annotations
          updatedAnnotations = [...updatedAnnotations, newConnection];

          // Update rfiTags state
          setRfiTags(
            pendingPhotoRef
              ? updatedRfiTags.filter(
                  (t) => t.connectionId !== newConnection.id,
                )
              : updatedRfiTags,
          );

          // Clear selected annotation index for new connections
          setSelectedAnnotationIndex(null);
        }

        // Update annotations state
        setAnnotations(updatedAnnotations);

        // Clear connection box state after annotation is saved
        setShowConnectionBox(false);
        setConnectionBoxStart(null);
        setConnectionBoxEnd(null);
        setConnectionText("");
        setCurrentTool(TOOLS.PAN); // Deactivate connection tool after saving
        setPendingPhotoRef(null);
      }

      // Ensure all RFI tags are synced to annotations before saving
      // Use updatedAnnotations if we just published a connection, otherwise use annotations
      const annotationsToSave =
        showConnectionBox && connectionBoxStart && connectionBoxEnd
          ? updatedAnnotations
          : annotations;

      const annotationsWithRfiTags = annotationsToSave.map((ann) => {
        if (ann.type === "connection") {
          const hasPhotoLink =
            annotationHasLinkedPhoto(ann) ||
            !!(ann.linkedPhotoUrl && typeof ann.linkedPhotoUrl === "string");
          if (hasPhotoLink) {
            return {
              ...ann,
              linkedRfiId: null,
              linkedRfiTagId: null,
              rfiTag: null,
            };
          }
          // If annotation already has rfiTag, preserve it but update from rfiTags state if linkedRfiTagId exists
          if (ann.linkedRfiTagId) {
            // Find the RFI tag in rfiTags state
            const rfiTag = rfiTags.find((tag) => tag.id === ann.linkedRfiTagId);
            if (rfiTag) {
              return {
                ...ann,
                rfiTag: {
                  id: rfiTag.id,
                  rfiId: rfiTag.rfiId,
                  rfiNumber: rfiTag.rfiNumber,
                  type: rfiTag.type,
                  ...(rfiTag.type === "punchlist"
                    ? { punchListId: rfiTag.punchListId }
                    : {}),
                  connectionId: rfiTag.connectionId || ann.id,
                  connectionPoint: rfiTag.connectionPoint,
                  tagPosition: rfiTag.tagPosition,
                  page: rfiTag.page,
                },
              };
            }
          }
          // If annotation has rfiTag already set, preserve it
          if (ann.rfiTag) {
            return ann;
          }
          // Check if there's an RFI tag in rfiTags state that matches this connection
          const matchingRfiTag = rfiTags.find(
            (tag) => tag.connectionId === ann.id,
          );
          if (matchingRfiTag) {
            return {
              ...ann,
              linkedRfiId: matchingRfiTag.rfiId,
              linkedRfiTagId: matchingRfiTag.id,
              rfiTag: {
                id: matchingRfiTag.id,
                rfiId: matchingRfiTag.rfiId,
                rfiNumber: matchingRfiTag.rfiNumber,
                type: matchingRfiTag.type,
                ...(matchingRfiTag.type === "punchlist"
                  ? { punchListId: matchingRfiTag.punchListId }
                  : {}),
                connectionId: matchingRfiTag.connectionId || ann.id,
                connectionPoint: matchingRfiTag.connectionPoint,
                tagPosition: matchingRfiTag.tagPosition,
                page: matchingRfiTag.page,
              },
            };
          }
        }
        // Return all other annotations as-is
        return ann;
      });

      let normalized;
      try {
        const fileMap = connectionPhotoFilesRef.current;
        normalized = await Promise.all(
          annotationsWithRfiTags.map((ann) =>
            normalizeConnectionPhotosForPersist(ann, projectId, fileMap),
          ),
        );
      } catch (err) {
        message.error(err?.message || "Failed to upload photo for save");
        return;
      }

      try {
        await mutationUpdate.mutateAsync({
          ...drawing,
          annotations: normalized,
        });
      } catch {
        // Photo upload may have succeeded; keep UI on normalized state so user can retry. Dirty vs saved stays correct.
        setAnnotations(normalized);
        return;
      }

      try {
        await syncPunchListDrawingLinksOnSave(
          lastSavedAnnotations,
          normalized,
          drawingId,
          {
            linkFn: linkPunchListItemToDrawing,
            unlinkFn: unlinkPunchListItemFromDrawing,
          },
        );
      } catch (err) {
        console.error("Failed to sync punch list drawing references:", err);
        message.warning(
          "Annotations saved, but syncing punch list drawing link(s) failed.",
        );
      }

      try {
        await syncRfiDrawingLinksOnSave(
          lastSavedAnnotations,
          normalized,
          drawingId,
          {
            linkFn: linkRfiToDrawing,
            unlinkFn: unlinkRfiFromDrawing,
          },
        );
      } catch (err) {
        console.error("Failed to sync RFI drawing references:", err);
        message.warning(
          "Annotations saved, but syncing RFI drawing link(s) failed.",
        );
      }

      annotationsWithRfiTags.forEach((ann) => {
        if (
          ann.type === "connection" &&
          ann.linkedPhotoPreviewUrl?.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(ann.linkedPhotoPreviewUrl);
          } catch (e) {}
        }
      });
      connectionPhotoFilesRef.current.clear();
      setAnnotations(normalized);
      setLastSavedAnnotations(normalized);
      setPersistedAnnotationIds(new Set(annotationIdsFrom(normalized)));
      skipUnsavedPromptRef.current = true;
      notification.success({ message: "Annotations updated successfully" });
      onClose();
      return;
    } else {
      setLastSavedAnnotations(annotations);
    }
    message.success("Annotations saved successfully");
  };

  const cleanupConnectionPhotoResources = (
    anns,
    pending,
    { clearAllFiles = false } = {},
  ) => {
    if (Array.isArray(anns)) {
      anns.forEach((ann) => {
        if (
          ann?.type === "connection" &&
          ann.linkedPhotoPreviewUrl?.startsWith("blob:")
        ) {
          try {
            URL.revokeObjectURL(ann.linkedPhotoPreviewUrl);
          } catch (e) {}
        }
        if (ann?.id) {
          connectionPhotoFilesRef.current.delete(ann.id);
        }
      });
    }
    if (pending?.objectUrl?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(pending.objectUrl);
      } catch (e) {}
    }
    if (clearAllFiles) {
      connectionPhotoFilesRef.current.clear();
    }
  };

  const resetStates = () => {
    cleanupConnectionPhotoResources(annotations, pendingPhotoRef, {
      clearAllFiles: true,
    });
    setPdfDoc(null);
    setPageIndex(0);
    setNumPages(0);
    setPageViewport(null);
    setAnnotations([]);
    setCurrentPath("");
    setColor("red");
    setStrokeWidth(2);
    setSelectedAnnotationIndex(null);
    setIsDrawing(false);
    setIsDrawingLoading(false);
    setCurrentTool(TOOLS.PAN);
    setCurrentShape(SHAPES.LINE);
    setTextInput("");
    setStartPoint(null);
    setDraggingIndex(null);
    setDragOffset({ x: 0, y: 0 });
    textInputPosRef.current = null;
    setTextInputPos(null);
    setShowTextInput(false);
    showTextInputRef.current = false;
    calloutTipRef.current = null;
    setCalloutTip(null);
    calloutKneeRef.current = null;
    setCalloutKnee(null);
    setCalloutDragEnd(null);
    setTextBold(false);
    setTextItalic(false);
    setTextUnderline(false);
    setTextStrike(false);
    setFontSize(16);
    setTextHighlight(false);
    setTextSquiggly(false);
    setLastSavedAnnotations([]);
    setZoom(1.0);
    setVisualZoom(1.0); // Reset visualZoom
    setDefaultZoom(1.0);
    setPdfPageOriginalWidth(null);
    setCloudDrawingActive(false);
    setCloudStartPoint(null);
    setActiveCloudGroupId(null);
    setSearchQuery("");
    setSearchResults([]);
    setCurrentMatchIndex(-1);
    setShowSearch(false);
    pdfTextCache.current = null;
    if (searchDebounceTimer.current) {
      clearTimeout(searchDebounceTimer.current);
    }
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch (e) {}
      renderTaskRef.current = null;
    }
    setPendingPhotoRef(null);
    setPhotoPreviewUrl(null);
  };

  const hasUnsavedAnnotations = useMemo(() => {
    const currentAnnotationsStr = JSON.stringify(annotations);
    const lastSavedAnnotationsStr = JSON.stringify(lastSavedAnnotations);
    const originalAnnotationsStr = JSON.stringify(drawing?.annotations || []);
    return (
      currentAnnotationsStr !== lastSavedAnnotationsStr &&
      currentAnnotationsStr !== originalAnnotationsStr
    );
  }, [annotations, lastSavedAnnotations, drawing?.annotations]);

  const shouldBlockRouterNavigation = useCallback(
    ({ currentLocation, nextLocation }) => {
      if (!visible || skipUnsavedPromptRef.current || !hasUnsavedAnnotations) return false;
      return (
        currentLocation.pathname !== nextLocation.pathname ||
        currentLocation.search !== nextLocation.search ||
        currentLocation.hash !== nextLocation.hash
      );
    },
    [visible, hasUnsavedAnnotations],
  );

  const navigationBlocker = useBlocker(shouldBlockRouterNavigation);

  const confirmUnsavedLeave = (onDiscard, onStay) =>
    Modal.confirm({
      title: "Unsaved Annotations",
      content:
        "You have unsaved annotations. Are you sure you want to close without saving?",
      okText: "Yes, Close",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        skipUnsavedPromptRef.current = true;
        resetStates();
        onDiscard();
      },
      onCancel: () => {
        onStay?.();
      },
    });

  useEffect(() => {
    if (!visible || !hasUnsavedAnnotations) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [visible, hasUnsavedAnnotations]);

  const handleCloseWithCheck = () => {
    if (skipUnsavedPromptRef.current) {
      resetStates();
      onClose();
    } else if (hasUnsavedAnnotations) {
      confirmUnsavedLeave(() => onClose());
    } else {
      resetStates();
      onClose();
    }
  };

  const handleDownloadWithAnnotations = async () => {
    try {
      setDownloading(true);
      await downloadPdfWithAnnotations(
        drawing?.file_url,
        annotations,
        drawing?.title,
        rfiTags,
        drawing?.drawing_number,
      );
    } catch (err) {
      // Error already handled in utility function
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadWithoutAnnotations = async () => {
    try {
      await downloadPdfWithoutAnnotations(
        drawing?.file_url,
        drawing?.title,
        drawing?.drawing_number,
      );
    } catch (err) {
      // Error already handled in utility function
    }
  };

  const handleHighlightToggle = () => {
    if (showTextInput) {
      setTextHighlight((v) => !v);
    } else if (
      selectedAnnotationIndex !== null &&
      annotations[selectedAnnotationIndex]?.type === "text"
    ) {
      setAnnotations((prev) =>
        prev.map((ann, i) =>
          i === selectedAnnotationIndex
            ? {
                ...ann,
                style: { ...ann.style, highlight: !ann.style?.highlight },
              }
            : ann,
        ),
      );
      setTextHighlight((v) => !v);
    }
  };

  const handleSquigglyToggle = () => {
    if (showTextInput) {
      setTextSquiggly((v) => !v);
    } else if (
      selectedAnnotationIndex !== null &&
      annotations[selectedAnnotationIndex]?.type === "text"
    ) {
      setAnnotations((prev) =>
        prev.map((ann, i) =>
          i === selectedAnnotationIndex
            ? {
                ...ann,
                style: { ...ann.style, squiggly: !ann.style?.squiggly },
              }
            : ann,
        ),
      );
      setTextSquiggly((v) => !v);
    }
  };

  const handleClearAnnotations = () => {
    if (!canClearAnnotations) {
      message.warning("Consultants cannot delete saved annotations.");
      return;
    }
    if (!canDeletePersistedAnnotations) {
      Modal.confirm({
        title: "Clear Unsaved Annotations",
        content:
          "Saved annotations will be kept. Unsaved marks from this session will be removed.",
        okText: "Clear Unsaved",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          const remaining = annotations.filter(
            (ann) => ann?.id && persistedAnnotationIds.has(ann.id),
          );
          cleanupConnectionPhotoResources(
            annotations.filter(
              (ann) => !ann?.id || !persistedAnnotationIds.has(ann.id),
            ),
            pendingPhotoRef,
          );
          setPendingPhotoRef(null);
          setAnnotations(remaining);
        },
      });
      return;
    }
    Modal.confirm({
      title: "Clear All Annotations",
      content:
        "Are you sure you want to delete all annotations? This action cannot be undone.",
      okText: "Yes, Clear",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        cleanupConnectionPhotoResources(annotations, pendingPhotoRef, {
          clearAllFiles: true,
        });
        setPendingPhotoRef(null);
        setAnnotations([]);
      },
    });
  };

  // Add useEffect to intercept Escape key and show unsaved changes warning
  useEffect(() => {
    if (!visible) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleCloseWithCheck();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [visible, annotations, lastSavedAnnotations]);

  const getTouchDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (currentTool && currentTool !== TOOLS.SELECT) {
      return;
    }
    if (e.touches.length === 2) {
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setTouchStartDistance(distance);
      setTouchStartZoom(visualZoom); // Use visualZoom as base

      // Calculate center point of pinch gesture relative to container
      if (pdfContainerRef.current && pageViewport) {
        const container = pdfContainerRef.current;
        const rect = container.getBoundingClientRect();
        // Center point relative to container viewport (visible area)
        const centerX =
          (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
        const centerY =
          (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
        setTouchStartCenter({ x: centerX, y: centerY });
        setTouchStartScroll({
          left: container.scrollLeft,
          top: container.scrollTop,
        });
      }
    }
  };

  const handleTouchMove = (e) => {
    if (currentTool && currentTool !== TOOLS.SELECT) {
      return;
    }
    if (e.touches.length === 2 && touchStartDistance !== null) {
      e.preventDefault();
      e.stopPropagation();
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDistance / touchStartDistance;
      const newZoom = Math.min(Math.max(touchStartZoom * scale, 0.1), 4);
      setVisualZoom(newZoom); // Update visualZoom only
    }
  };

  const handleTouchEnd = (e) => {
    if (currentTool && currentTool !== TOOLS.SELECT) {
      return;
    }
    if (
      e.touches.length < 2 &&
      touchStartDistance !== null &&
      touchStartCenter &&
      touchStartScroll
    ) {
      const finalZoom = visualZoom; // Use final visualZoom
      setZoom(finalZoom); // Commit to actual zoom for re-render
      const zoomRatio = finalZoom / touchStartZoom;
      const contentXAtStart = touchStartScroll.left + touchStartCenter.x;
      const contentYAtStart = touchStartScroll.top + touchStartCenter.y;

      // After zoom, this point moves: newPos = oldPos * zoomRatio
      const contentXAtNewZoom = contentXAtStart * zoomRatio;
      const contentYAtNewZoom = contentYAtStart * zoomRatio;
      const newScrollLeft = contentXAtNewZoom - touchStartCenter.x;
      const newScrollTop = contentYAtNewZoom - touchStartCenter.y;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (pdfContainerRef.current && pageViewport) {
              const container = pdfContainerRef.current;
              const currentContentWidth = pageViewport.width;
              const currentContentHeight = pageViewport.height;
              const maxScrollLeft = Math.max(
                0,
                currentContentWidth - container.clientWidth,
              );
              const maxScrollTop = Math.max(
                0,
                currentContentHeight - container.clientHeight,
              );
              const finalScrollLeft = Math.max(
                0,
                Math.min(newScrollLeft, maxScrollLeft),
              );
              const finalScrollTop = Math.max(
                0,
                Math.min(newScrollTop, maxScrollTop),
              );

              container.scrollLeft = finalScrollLeft;
              container.scrollTop = finalScrollTop;
            }
          });
        });
      });

      setTouchStartDistance(null);
      setTouchStartZoom(null);
      setTouchStartCenter(null);
      setTouchStartScroll(null);
    }
  };

  const handleSvgTouchStart = (e) => {
    if (e.touches.length === 1 && currentTool && currentTool !== TOOLS.SELECT) {
      e.preventDefault();
      e.stopPropagation();
      const syntheticEvent = {
        ...e,
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      startDrawing(syntheticEvent);
    }
  };

  const handleSvgTouchMove = (e) => {
    if (
      e.touches.length === 1 &&
      isDrawing &&
      currentTool &&
      currentTool !== TOOLS.SELECT
    ) {
      e.preventDefault();
      e.stopPropagation();
      const syntheticEvent = {
        ...e,
        clientX: e.touches[0].clientX,
        clientY: e.touches[0].clientY,
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      draw(syntheticEvent);
    }
  };

  const handleSvgTouchEnd = (e) => {
    if (
      e.changedTouches.length === 1 &&
      currentTool &&
      currentTool !== TOOLS.SELECT
    ) {
      e.preventDefault();
      e.stopPropagation();
      const syntheticEvent = {
        ...e,
        clientX: e.changedTouches[0].clientX,
        clientY: e.changedTouches[0].clientY,
        preventDefault: () => {},
        stopPropagation: () => {},
      };
      endDrawing(syntheticEvent);
    }
  };

  const getModalWidth = () => {
    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth < 1024;

    if (isMobile) {
      return windowWidth - 32;
    }
    if (isTablet) {
      return Math.min(windowWidth - 64, 900);
    }

    if (pdfPageOriginalWidth && pdfPageOriginalWidth + 48 > 1000) {
      return pdfPageOriginalWidth + 48;
    }
    return 1000;
  };

  const handleCreateLinkedItemClose = (type) => {
    if (type === "rfi") {
      setShowCreateRfiModal(false);
      queryClient.invalidateQueries(["rfis-simple", projectId]);
    } else {
      setShowCreateSubmittalModal(false);
      queryClient.invalidateQueries(["submittals-simple", projectId]);
    }
  };

  const handleRfiCreated = (createdRfi) => {
    const createdItemId = createdRfi?.id ?? createdRfi?._id;
    if (!createdItemId) {
      return;
    }
    setCreatedRfiForLink(createdRfi);
    setSelectedRfiId(createdItemId);
    setIsLinkSelectOpen(false);
  };

  const handleSubmittalCreated = (createdSubmittal) => {
    const createdItemId = createdSubmittal?.id ?? createdSubmittal?._id;
    if (!createdItemId) {
      return;
    }
    setCreatedSubmittalForLink(createdSubmittal);
    setSelectedRfiId(createdItemId);
    setIsLinkSelectOpen(false);
  };

  const handleOpenCreateLinkedItem = () => {
    setIsLinkSelectOpen(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setTimeout(() => {
      if (linkType === "rfi") {
        setShowCreateRfiModal(true);
      } else {
        setShowCreateSubmittalModal(true);
      }
    }, 0);
  };

  const previewHeight = "70vh";
  const previewScrollHeight = "calc(70vh - 64px)";

  return (
    <div
      style={
        embedded
          ? { display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }
          : undefined
      }
    >
      <Modal
        title="Unsaved Annotations"
        open={navigationBlocker.state === "blocked"}
        zIndex={1100}
        closable={false}
        maskClosable={false}
        keyboard={false}
        okText="Yes, Close"
        okType="danger"
        cancelText="Cancel"
        centered
        onOk={() => {
          resetStates();
          navigationBlocker.proceed?.();
        }}
        onCancel={() => {
          navigationBlocker.reset?.();
        }}
      >
        You have unsaved annotations. Are you sure you want to close without
        saving?
      </Modal>
      <Modal
        title={
          linkType === "rfi"
            ? "Link to RFI"
            : linkType === "submittal"
              ? "Link to Submittal"
              : linkType === "drawing"
                ? "Link to Drawing"
                : "Link to punch list item"
        }
        open={showLinkModal}
        onCancel={() => {
          if (
            showCreateRfiModal ||
            showCreateSubmittalModal ||
            showCreatePunchListItemModal
          ) {
            return;
          }
          setIsLinkSelectOpen(false);
          setIsPunchListFolderSelectOpen(false);
          setIsPunchListItemSelectOpen(false);
          setShowLinkModal(false);
          setSelectedRfiId(null);
          setLinkModalPunchListId(null);
          setCreatedPunchListItemForLink(null);
          setCreatedRfiForLink(null);
          setCreatedSubmittalForLink(null);
          setDrawingSearch("");
          if (
            selectedAnnotationIndex !== null &&
            isAutoGeneratedDrawingLink(annotations[selectedAnnotationIndex])
          ) {
            setSelectedAnnotationIndex(null);
          }
          setLinkType(null);
        }}
        onOk={() => {
          if (linkType === "drawing") {
            handleConfirmDrawingLink();
            return;
          }
          const hasLinkSelection =
            linkType === "punchlist"
              ? !!(linkModalPunchListId && selectedRfiId)
              : !!selectedRfiId;
          if (hasLinkSelection) {
            setPendingPhotoRef(null);
            // Find selected item details (RFI or Submittal)
            let selectedItem, itemNumber, itemType;
            if (linkType === "rfi") {
              selectedItem = rfisForLink.find(
                (rfi) => String(rfi.id ?? rfi._id) === String(selectedRfiId),
              );
              itemNumber = selectedItem?.number || selectedRfiId;
              itemType = "rfi";
            } else if (linkType === "submittal") {
              selectedItem = submittalsForLink.find(
                (submittal) =>
                  String(submittal.id ?? submittal._id) === String(selectedRfiId),
              );
              // Format submittal number as {spec_section_number}-{number}.{revision}
              const specSection = selectedItem?.spec_section_number || "";
              const number = selectedItem?.number || "";
              const revision = selectedItem?.revision || 0;
              itemNumber = specSection
                ? `${specSection}-${number}.${revision}`
                : `${number}.${revision}`;
              itemType = "submittal";
            } else {
              const items = punchListItemsForLink;
              const itemIdStr = String(selectedRfiId);
              selectedItem = items.find(
                (it) => String(it.id ?? it._id) === itemIdStr,
              );
              const n = selectedItem?.number ?? "";
              const title = selectedItem?.title || "";
              const shortTitle =
                title.length > 22 ? `${title.slice(0, 22)}…` : title;
              itemNumber =
                `${n}${shortTitle ? ` · ${shortTitle}` : ""}`.trim() ||
                itemIdStr;
              itemType = "punchlist";
            }

            // Calculate connection box right edge center for tag connection point
            let connectionCenter = null;
            let connectionAnnotationId = null;
            const selectedLinkAnn =
              selectedAnnotationIndex !== null
                ? annotations[selectedAnnotationIndex]
                : null;
            const isEditingLinkBox = selectedLinkAnn?.type === "connection";
            const isNewConnection =
              connectionBoxStart && connectionBoxEnd && !isEditingLinkBox;

            if (connectionBoxStart && connectionBoxEnd) {
              const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
              const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
              const boxWidth = Math.abs(
                connectionBoxEnd.x - connectionBoxStart.x,
              );
              const boxHeight = Math.abs(
                connectionBoxEnd.y - connectionBoxStart.y,
              );
              // Connection point at the inner rectangle edge (inner rectangle is inset by 8px)
              const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
              connectionCenter = {
                x: boxX + boxWidth - innerRectInset, // Inner right edge
                y: boxY + boxHeight - innerRectInset, // Inner bottom edge
              };
              // Find connection annotation ID if exists, or generate new one for new connections
              if (isEditingLinkBox) {
                connectionAnnotationId = selectedLinkAnn.id;
              } else if (isNewConnection) {
                // Generate new ID for the new connection annotation
                connectionAnnotationId = uuidv4();
              }
            } else if (isEditingLinkBox) {
              const ann = annotations[selectedAnnotationIndex];
              const boxX = Math.min(ann.start.x, ann.end.x);
              const boxY = Math.min(ann.start.y, ann.end.y);
              const boxWidth = Math.abs(ann.end.x - ann.start.x);
              const boxHeight = Math.abs(ann.end.y - ann.start.y);
              // Connection point at the inner rectangle edge (inner rectangle is inset by 8px)
              const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
              connectionCenter = {
                x: boxX + boxWidth - innerRectInset, // Inner right edge
                y: boxY + boxHeight - innerRectInset, // Inner bottom edge
              };
              connectionAnnotationId = ann.id;
            }

            if (connectionCenter) {
              // If connection already has an RFI tag, remove the old one first
              if (isEditingLinkBox) {
                const existingAnnotation = selectedLinkAnn;
                if (existingAnnotation.linkedRfiTagId) {
                  // Remove old RFI tag
                  setRfiTags((prev) =>
                    prev.filter(
                      (tag) => tag.id !== existingAnnotation.linkedRfiTagId,
                    ),
                  );
                }
              } else if (connectionBoxStart && connectionBoxEnd) {
                // For unsaved connection boxes, remove any pending tags in proximity
                const boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
                const boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
                const boxWidth = Math.abs(
                  connectionBoxEnd.x - connectionBoxStart.x,
                );
                const boxHeight = Math.abs(
                  connectionBoxEnd.y - connectionBoxStart.y,
                );
                const innerRectInset = CONNECTION_INNER_RECT_INSET;
                const centerX = boxX + boxWidth - innerRectInset;
                const centerY = boxY + boxHeight - innerRectInset;

                setRfiTags((prev) =>
                  prev.filter((tag) => {
                    // Keep tags that have a connectionId (belong to saved connections)
                    if (tag.connectionId) return true;
                    // Keep tags on different pages
                    if (tag.page !== pageIndex) return true;
                    // Remove pending tags near this connection box
                    const distance = Math.sqrt(
                      Math.pow(tag.connectionPoint.x - centerX, 2) +
                        Math.pow(tag.connectionPoint.y - centerY, 2),
                    );
                    return distance >= 50; // Keep if distance >= 50
                  }),
                );
              }

              // Calculate box dimensions for tag positioning
              let boxX, boxY, boxWidth, boxHeight;
              if (connectionBoxStart && connectionBoxEnd) {
                boxX = Math.min(connectionBoxStart.x, connectionBoxEnd.x);
                boxY = Math.min(connectionBoxStart.y, connectionBoxEnd.y);
                boxWidth = Math.abs(connectionBoxEnd.x - connectionBoxStart.x);
                boxHeight = Math.abs(connectionBoxEnd.y - connectionBoxStart.y);
              } else if (isEditingLinkBox) {
                const ann = selectedLinkAnn;
                boxX = Math.min(ann.start.x, ann.end.x);
                boxY = Math.min(ann.start.y, ann.end.y);
                boxWidth = Math.abs(ann.end.x - ann.start.x);
                boxHeight = Math.abs(ann.end.y - ann.start.y);
              } else {
                // Fallback - use connectionCenter to estimate box
                boxX = connectionCenter.x - 100;
                boxY = connectionCenter.y - 50;
                boxWidth = 200;
                boxHeight = 100;
              }

              // Tag position: tag should be positioned so its bottom edge aligns with inner rect bottom
              const innerRectInset = CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
              const tagHeight = RFI_TAG_HEIGHT; // Tag height
              const tagStart = {
                x: boxX + boxWidth - innerRectInset, // Inner right edge
                y: boxY + boxHeight - innerRectInset - tagHeight / 2, // Center tag vertically at inner rect bottom
              };

              // Connection point: bottom right corner of inner rectangle (where tag is positioned)
              const connectionPoint = {
                x: boxX + boxWidth - innerRectInset, // Inner right edge
                y: boxY + boxHeight - innerRectInset, // Inner bottom edge
              };

              const newTag = {
                id: uuidv4(),
                rfiId: selectedRfiId,
                rfiNumber: itemNumber,
                type: itemType,
                ...(itemType === "punchlist"
                  ? { punchListId: linkModalPunchListId }
                  : {}),
                connectionId: connectionAnnotationId,
                connectionPoint: connectionPoint,
                tagPosition: tagStart,
                page: pageIndex,
              };

              setRfiTags((prev) => [...prev, newTag]);

              // Save link to connection annotation with full tag data
              if (isEditingLinkBox) {
                const prevConn = selectedLinkAnn;
                if (prevConn?.linkedPhotoPreviewUrl?.startsWith("blob:")) {
                  try {
                    URL.revokeObjectURL(prevConn.linkedPhotoPreviewUrl);
                  } catch (e) {}
                }
                connectionPhotoFilesRef.current.delete(prevConn.id);
                setAnnotations((prev) =>
                  prev.map((ann, i) =>
                    i === selectedAnnotationIndex
                      ? {
                          id: ann.id,
                          user: ann.user,
                          type: "connection",
                          start: connectionBoxStart || ann.start,
                          end: connectionBoxEnd || ann.end,
                          content: "",
                          page: pageIndex,
                          linkedRfiId: selectedRfiId,
                          linkedRfiTagId: newTag.id,
                          linkedPhoto: null,
                          linkedPhotoUrl: null,
                          linkedPhotoName: null,
                          linkedPhotoPreviewUrl: null,
                          linkedPhotoTagId: null,
                          photoTag: null,
                          targetDrawingId: undefined,
                          targetDrawingNumber: undefined,
                          targetFolderId: undefined,
                          calloutKind: undefined,
                          autoGenerated: undefined,
                          rfiTag: {
                            id: newTag.id,
                            rfiId: selectedRfiId,
                            rfiNumber: itemNumber,
                            type: itemType,
                            ...(itemType === "punchlist"
                              ? { punchListId: linkModalPunchListId }
                              : {}),
                            connectionId: connectionAnnotationId,
                            connectionPoint: connectionCenter,
                            tagPosition: tagStart,
                            page: pageIndex,
                          },
                        }
                      : ann,
                  ),
                );
              } else if (isNewConnection) {
                // This is a new unsaved connection box - create and save the annotation
                const newAnnotation = {
                  id: connectionAnnotationId, // Use the pre-generated ID
                  type: "connection",
                  start: connectionBoxStart,
                  end: connectionBoxEnd,
                  content: connectionText || "",
                  color: color,
                  page: pageIndex,
                  linkedRfiId: selectedRfiId,
                  linkedRfiTagId: newTag.id,
                  linkedPhoto: null,
                  linkedPhotoUrl: null,
                  linkedPhotoName: null,
                  linkedPhotoPreviewUrl: null,
                  linkedPhotoTagId: null,
                  photoTag: null,
                  rfiTag: {
                    id: newTag.id,
                    rfiId: selectedRfiId,
                    rfiNumber: itemNumber,
                    type: itemType,
                    ...(itemType === "punchlist"
                      ? { punchListId: linkModalPunchListId }
                      : {}),
                    connectionId: connectionAnnotationId,
                    connectionPoint: connectionCenter,
                    tagPosition: tagStart,
                    page: pageIndex,
                  },
                };

                setAnnotations((prev) => [...prev, newAnnotation]);

                // Clear the connection box states after saving
                setShowConnectionBox(false);
                setConnectionBoxStart(null);
                setConnectionBoxEnd(null);
                setConnectionText("");
                setCurrentTool(TOOLS.PAN);
              }
            }

            setShowLinkModal(false);
            setSelectedRfiId(null);
            setLinkModalPunchListId(null);
            setCreatedPunchListItemForLink(null);
            setIsPunchListFolderSelectOpen(false);
            setIsPunchListItemSelectOpen(false);
            setLinkType(null);
          } else {
            message.warning(
              linkType === "punchlist"
                ? "Please select a punch list and an item"
                : `Please select ${linkType === "rfi" ? "an RFI" : "a Submittal"}`,
            );
          }
        }}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          {linkType === "drawing" ? (
            <>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Select drawing:
              </label>
              <Select
                showSearch
                placeholder="Search by drawing number or title"
                filterOption={false}
                onSearch={(value) => setDrawingSearch(value)}
                loading={drawingsSearchLoading}
                value={selectedRfiId ? String(selectedRfiId) : undefined}
                open={isLinkSelectOpen}
                onDropdownVisibleChange={setIsLinkSelectOpen}
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                onChange={(value) => {
                  setSelectedRfiId(value);
                  setIsLinkSelectOpen(false);
                }}
                style={{ width: "100%" }}
                options={(() => {
                  const options = drawingsForLink
                    .filter(
                      (item) =>
                        String(item.id ?? item._id) !== String(drawingId),
                    )
                    .map((item) => ({
                      value: String(item.id ?? item._id),
                      label: `${item.drawing_number || "—"} — ${item.title || "Untitled"}`,
                    }));
                  const current =
                    selectedAnnotationIndex !== null
                      ? annotations[selectedAnnotationIndex]
                      : null;
                  const currentId = current?.targetDrawingId
                    ? String(current.targetDrawingId)
                    : "";
                  if (
                    isDrawingLink(current) &&
                    currentId &&
                    !options.some((opt) => opt.value === currentId)
                  ) {
                    options.unshift({
                      value: currentId,
                      label: `${current.targetDrawingNumber || "Sheet"}`,
                    });
                  }
                  return options;
                })()}
              />
            </>
          ) : linkType === "punchlist" ? (
            <>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Select punch list:
              </label>
              <Select
                showSearch
                placeholder="Search and select a punch list"
                optionFilterProp="label"
                filterOption={filterOption}
                loading={punchListsLoading}
                open={isPunchListFolderSelectOpen}
                onDropdownVisibleChange={setIsPunchListFolderSelectOpen}
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                value={linkModalPunchListId}
                onChange={(id) => {
                  setLinkModalPunchListId(id);
                  setSelectedRfiId(null);
                  setCreatedPunchListItemForLink(null);
                  setIsPunchListFolderSelectOpen(false);
                }}
                style={{ width: "100%", marginBottom: 16 }}
                options={(punchListsData?.punchLists || []).map((pl) => ({
                  value: pl.id ?? pl._id,
                  label: pl.title || "Untitled",
                }))}
              />
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                Select item:
              </label>
              <Select
                showSearch
                placeholder={
                  linkModalPunchListId
                    ? "Search and select a punch list item"
                    : "Choose a punch list first"
                }
                optionFilterProp="label"
                filterOption={filterOption}
                loading={punchListItemsLoading}
                disabled={!linkModalPunchListId}
                open={isPunchListItemSelectOpen}
                onDropdownVisibleChange={setIsPunchListItemSelectOpen}
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                value={selectedRfiId}
                onChange={(value) => {
                  setSelectedRfiId(value);
                  setIsPunchListItemSelectOpen(false);
                }}
                style={{ width: "100%" }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        padding: "8px",
                      }}
                    >
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={handleOpenCreatePunchListItem}
                        style={{ padding: 0 }}
                      >
                        Create punch list item
                      </Button>
                    </div>
                  </>
                )}
                options={punchListItemsForLink.map((it) => {
                  const id = it.id ?? it._id;
                  return {
                    value: id,
                    label: `#${it.number ?? ""} — ${it.title || "No title"}`,
                  };
                })}
              />
            </>
          ) : (
            <>
              <label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
                {linkType === "rfi" ? "Select RFI:" : "Select Submittal:"}
              </label>
              <Select
                showSearch
                placeholder={
                  linkType === "rfi"
                    ? "Search and select an RFI"
                    : "Search and select a Submittal"
                }
                optionFilterProp="label"
                filterOption={filterOption}
                loading={linkType === "rfi" ? rfisLoading : submittalsLoading}
                value={selectedRfiId}
                open={isLinkSelectOpen}
                onDropdownVisibleChange={setIsLinkSelectOpen}
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                onChange={(value) => {
                  setSelectedRfiId(value);
                  setIsLinkSelectOpen(false);
                }}
                style={{ width: "100%" }}
                dropdownRender={(menu) => (
                  <>
                    {menu}
                    <div
                      style={{
                        borderTop: "1px solid #f0f0f0",
                        padding: "8px",
                      }}
                    >
                      <Button
                        type="link"
                        icon={<PlusOutlined />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={handleOpenCreateLinkedItem}
                        style={{ padding: 0 }}
                      >
                        {linkType === "rfi" ? "Create RFI" : "Create Submittal"}
                      </Button>
                    </div>
                  </>
                )}
                options={
                  linkType === "rfi"
                    ? rfisForLink.map((rfi) => ({
                        value: rfi.id ?? rfi._id,
                        label: `RFI #${rfi.number || ""} - ${rfi.title || "No Title"}`,
                      }))
                    : submittalsForLink.map((submittal) => {
                        const specSection = submittal.spec_section_number || "";
                        const number = submittal.number || "";
                        const revision = submittal.revision || 0;
                        const formattedNumber = specSection
                          ? `${specSection}-${number}.${revision}`
                          : `${number}.${revision}`;
                        return {
                          value: submittal.id ?? submittal._id,
                          label: `${formattedNumber} - ${submittal.title || "No Title"}`,
                        };
                      })
                }
              />
            </>
          )}
        </div>
      </Modal>
      <input
        ref={photoLinkInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (!file || !file.type.startsWith("image/")) {
            if (file) message.warning("Please choose an image file");
            return;
          }
          const objectUrl = URL.createObjectURL(file);
          applyPhotoToConnectionDraft({ file, objectUrl, filename: file.name });
        }}
      />
      <Modal
        title="Linked photo"
        open={!!photoPreviewUrl}
        onCancel={() => setPhotoPreviewUrl(null)}
        footer={null}
        width={720}
        centered
      >
        {photoPreviewUrl && (
          <img
            alt="Linked"
            src={photoPreviewUrl}
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              display: "block",
              margin: "0 auto",
              objectFit: "contain",
            }}
          />
        )}
      </Modal>
      <CreateRFIModal
        visible={showCreateRfiModal}
        onClose={() => handleCreateLinkedItemClose("rfi")}
        initialData={drawingLinkInitialRfiData}
        onCreated={handleRfiCreated}
      />
      <CreateSubmittalModal
        visible={showCreateSubmittalModal}
        onClose={() => handleCreateLinkedItemClose("submittal")}
        initialData={drawingLinkInitialSubmittalData}
        onCreated={handleSubmittalCreated}
      />
      {!!linkModalPunchListId && (
        <CreatePunchListItemModal
          visible={showCreatePunchListItemModal}
          onClose={handleCreatePunchListItemClose}
          punchListId={linkModalPunchListId}
          onCreated={handlePunchListItemCreated}
        />
      )}
      <DrawingPreviewShell
        embedded={embedded}
        title={drawing?.title || "Drawing Preview"}
        visible={visible}
        onCancel={handleCloseWithCheck}
        hideEmbeddedTopBar={hideEmbeddedTopBar}
        width={getModalWidth()}
        footer={
          <DrawingFooter
            pageIndex={pageIndex}
            numPages={numPages}
            windowWidth={windowWidth}
            onPreviousPage={goToPreviousPage}
            onNextPage={goToNextPage}
            onDownloadWithAnnotations={handleDownloadWithAnnotations}
            onDownloadWithoutAnnotations={handleDownloadWithoutAnnotations}
            onSave={handleSave}
            onClose={handleCloseWithCheck}
            onClearAnnotations={handleClearAnnotations}
            canClearAnnotations={canClearAnnotations}
            saving={savingAnnotations}
            detectingSheetLinks={detectingSheetLinks}
          />
        }
      >
        {(downloading || isDrawingLoading) && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255,255,255,0.7)",
              zIndex: 1000,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spin
              size="large"
              tip={downloading ? "Preparing PDF..." : "Loading drawing..."}
            />
          </div>
        )}
        <div
          ref={modalContentRef}
          style={
            embedded
              ? {
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "row",
                  overflow: "hidden",
                  paddingRight: 0,
                  paddingBottom: 0,
                  background: "#fff",
                }
              : {
                  height: previewHeight,
                  display: "flex",
                  flexDirection: "row",
                  overflow: "hidden",
                  paddingRight: 0,
                  paddingBottom: 0,
                  background: "#fff",
                }
          }
        >
          <PageThumbnailSidebar
            drawings={folderDrawings}
            activeDrawingId={drawingId}
            activePageIndex={pageIndex}
            activePdfDoc={pdfDoc}
            activeNumPages={numPages}
            onSelectDrawing={handleSelectFolderDrawing}
            open={pageThumbsOpen}
            onOpenChange={setPageThumbsOpen}
          />
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
          <div style={{ flexShrink: 0 }}>
            <DrawingToolbar
              currentTool={currentTool}
              currentShape={currentShape}
              color={color}
              strokeWidth={strokeWidth}
              textBold={textBold}
              textItalic={textItalic}
              textUnderline={textUnderline}
              textStrike={textStrike}
              fontSize={fontSize}
              showTextInput={showTextInput}
              selectedAnnotationIndex={selectedAnnotationIndex}
              canDeleteSelectedAnnotation={canDeleteSelectedAnnotation}
              zoom={zoom}
              visualZoom={visualZoom}
              showSearch={showSearch}
              searchQuery={searchQuery}
              searchResults={searchResults}
              currentMatchIndex={currentMatchIndex}
              isSearching={isSearching}
              searchInputRef={searchInputRef}
              onToolClick={handleToolClick}
              detectingSheetLinks={detectingSheetLinks}
              canDetectSheetLinks={!savingAnnotations}
              onDetectSheetLinks={canUseSheetLinkDetection ? handleDetectSheetLinks : undefined}
              onShapeClick={(shape) => {
                if (
                  currentShape === SHAPES.CLOUD &&
                  shape !== SHAPES.CLOUD &&
                  cloudDrawingActive
                ) {
                  finalizeCloudDrawing();
                }
                if (currentShape === shape) {
                  if (shape === SHAPES.CLOUD && cloudDrawingActive) {
                    finalizeCloudDrawing();
                  }
                  setCurrentShape(null);
                } else {
                  setCurrentShape(shape);
                }
              }}
              onColorChange={(c) => {
                setColor(c);
                if (
                  showTextInput &&
                  selectedAnnotationIndex !== null &&
                  annotations[selectedAnnotationIndex]?.type === "text"
                ) {
                  setAnnotations((prev) =>
                    prev.map((ann, i) =>
                      i === selectedAnnotationIndex ? { ...ann, color: c } : ann,
                    ),
                  );
                }
              }}
              onStrokeWidthChange={setStrokeWidth}
              onTextStyleChange={(style, value) => {
                if (style === "bold") {
                  setTextBold(value);
                  if (selectedAnnotationIndex !== null) {
                    setAnnotations((prev) =>
                      prev.map((ann, i) =>
                        i === selectedAnnotationIndex
                          ? {
                              ...ann,
                              style: {
                                ...ann.style,
                                fontWeight: value ? "bold" : "normal",
                              },
                            }
                          : ann,
                      ),
                    );
                  }
                } else if (style === "italic") {
                  setTextItalic(value);
                  if (selectedAnnotationIndex !== null) {
                    setAnnotations((prev) =>
                      prev.map((ann, i) =>
                        i === selectedAnnotationIndex
                          ? {
                              ...ann,
                              style: {
                                ...ann.style,
                                fontStyle: value ? "italic" : "normal",
                              },
                            }
                          : ann,
                      ),
                    );
                  }
                } else if (style === "underline") {
                  setTextUnderline(value);
                  if (selectedAnnotationIndex !== null) {
                    setAnnotations((prev) =>
                      prev.map((ann, i) =>
                        i === selectedAnnotationIndex
                          ? {
                              ...ann,
                              style: {
                                ...ann.style,
                                textDecoration: `${value ? "underline " : ""}${
                                  textStrike ? "line-through" : ""
                                }`.trim(),
                              },
                            }
                          : ann,
                      ),
                    );
                  }
                } else if (style === "strike") {
                  setTextStrike(value);
                  if (selectedAnnotationIndex !== null) {
                    setAnnotations((prev) =>
                      prev.map((ann, i) =>
                        i === selectedAnnotationIndex
                          ? {
                              ...ann,
                              style: {
                                ...ann.style,
                                textDecoration: `${
                                  textUnderline ? "underline " : ""
                                }${value ? "line-through" : ""}`.trim(),
                              },
                            }
                          : ann,
                      ),
                    );
                  }
                }
              }}
              onFontSizeChange={(size) => {
                setFontSize(size);
                if (
                  showTextInput &&
                  selectedAnnotationIndex !== null &&
                  annotations[selectedAnnotationIndex]?.type === "text"
                ) {
                  setAnnotations((prev) =>
                    prev.map((ann, i) =>
                      i === selectedAnnotationIndex
                        ? { ...ann, fontSize: size }
                        : ann,
                    ),
                  );
                }
              }}
              onDeleteAnnotation={deleteSelectedAnnotation}
              onZoomChange={(newZoom) => {
                const prev = zoomRef.current;
                if (newZoom === prev) return;
                const container = pdfContainerRef.current;
                if (container) {
                  zoomScrollAnchorRef.current = {
                    fx: container.clientWidth / 2,
                    fy: container.clientHeight / 2,
                    oldZoom: prev,
                    newZoom,
                  };
                }
                zoomRef.current = newZoom;
                setZoom(newZoom);
                setVisualZoom(newZoom);
              }}
              onSearchToggle={() => {
                setShowSearch(!showSearch);
                if (!showSearch) {
                  setTimeout(() => {
                    searchInputRef.current?.focus();
                  }, 100);
                } else {
                  setSearchQuery("");
                  setSearchResults([]);
                  setCurrentMatchIndex(-1);
                }
              }}
              onSearchChange={handleSearchChange}
              onSearchNavigate={(direction) => {
                if (direction === "next") {
                  goToNextMatch();
                } else {
                  goToPreviousMatch();
                }
              }}
            />
          </div>
          <div
            ref={pdfContainerRef}
            style={{
              width: "100%",
              ...(embedded
                ? { flex: 1, minHeight: 0 }
                : { height: previewScrollHeight }),
              overflowX: "auto",
              overflowY: "auto",
              background: "#fff",
              touchAction:
                currentTool && currentTool !== TOOLS.SELECT && !contentOverflows
                  ? "none"
                  : "pan-x pan-y",
              WebkitOverflowScrolling: "touch",
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              style={{
                position: "relative",
                width: pageViewport?.width || undefined,
                minWidth: pageViewport?.width || undefined,
                height: pageViewport?.height || undefined,
                overflow: "visible",
                margin: "0 auto",
                zIndex: 0,
              }}
            >
              <canvas
                ref={canvasRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: pageViewport?.width || undefined,
                  height: pageViewport?.height || undefined,
                  zIndex: 1,
                  transform: `scale(${visualZoom / zoom})`, // Scale visually based on ratio
                  transformOrigin: "top left",
                }}
              />
              <svg
                ref={svgRef}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: pageViewport?.width || undefined,
                  height: pageViewport?.height || undefined,
                  touchAction:
                    currentTool && currentTool !== TOOLS.SELECT
                      ? "none"
                      : contentOverflows || visualZoom > 1.0
                        ? "pan-x pan-y"
                        : "none",
                  zIndex: 2,
                  transform: `translateZ(0) scale(${visualZoom / zoom})`, // Scale visually based on ratio
                  willChange: "transform",
                  transformOrigin: "top left",
                  cursor:
                    currentTool === TOOLS.PAN
                      ? isPanning
                        ? "grabbing"
                        : "grab"
                      : currentTool === TOOLS.SELECT
                        ? "default"
                        : "crosshair",
                }}
                onMouseDown={(e) => {
                  // Close connection box if clicking outside
                  if (
                    showConnectionBox &&
                    connectionBoxStart &&
                    connectionBoxEnd
                  ) {
                    const pt = getMousePos(e);
                    const boxX = Math.min(
                      connectionBoxStart.x,
                      connectionBoxEnd.x,
                    );
                    const boxY = Math.min(
                      connectionBoxStart.y,
                      connectionBoxEnd.y,
                    );
                    const boxWidth = Math.abs(
                      connectionBoxEnd.x - connectionBoxStart.x,
                    );
                    const boxHeight = Math.abs(
                      connectionBoxEnd.y - connectionBoxStart.y,
                    );

                    // Check if clicking outside the box
                    if (
                      pt.x < boxX ||
                      pt.x > boxX + boxWidth ||
                      pt.y < boxY ||
                      pt.y > boxY + boxHeight
                    ) {
                      // Save or update connection - same logic as publish button
                      if (connectionBoxStart && connectionBoxEnd) {
                        if (
                          selectedAnnotationIndex !== null &&
                          annotations[selectedAnnotationIndex]?.type ===
                            "connection"
                        ) {
                          // Update existing connection annotation (preserve rfiTag if exists)
                          const updateBoxX = Math.min(
                            connectionBoxStart.x,
                            connectionBoxEnd.x,
                          );
                          const updateBoxY = Math.min(
                            connectionBoxStart.y,
                            connectionBoxEnd.y,
                          );
                          const updateBoxWidth = Math.abs(
                            connectionBoxEnd.x - connectionBoxStart.x,
                          );
                          const updateBoxHeight = Math.abs(
                            connectionBoxEnd.y - connectionBoxStart.y,
                          );
                          const strokeWidth = 2;
                          const tagHeight = RFI_TAG_HEIGHT; // Tag height
                          const newConnectionCenter = {
                            x: updateBoxX + updateBoxWidth - strokeWidth, // Right edge
                            y: updateBoxY + updateBoxHeight - strokeWidth, // Bottom right corner
                          };
                          // Tag position: tag should be positioned so its bottom edge aligns with box bottom
                          const newTagPosition = {
                            x: newConnectionCenter.x,
                            y: updateBoxY + updateBoxHeight - tagHeight / 2, // Center tag vertically so bottom edge is at box bottom
                          };

                          let existingRfiTag = null;
                          setAnnotations((prev) => {
                            const prevAnn = prev[selectedAnnotationIndex];
                            const hasPhotoLink =
                              !!prevAnn?.linkedPhotoUrl ||
                              !!prevAnn?.photoTag ||
                              annotationHasLinkedPhoto(prevAnn);
                            existingRfiTag = hasPhotoLink
                              ? null
                              : prevAnn?.rfiTag;

                            return prev.map((ann, i) =>
                              i === selectedAnnotationIndex
                                ? {
                                    ...ann,
                                    start: connectionBoxStart,
                                    end: connectionBoxEnd,
                                    content: "", // No textarea, just selection rectangle
                                    rfiTag: hasPhotoLink
                                      ? null
                                      : prevAnn.rfiTag
                                        ? {
                                            ...prevAnn.rfiTag,
                                            connectionPoint:
                                              newConnectionCenter,
                                            tagPosition: newTagPosition,
                                          }
                                        : ann.rfiTag,
                                    linkedRfiId: hasPhotoLink
                                      ? null
                                      : prevAnn.linkedRfiId || ann.linkedRfiId,
                                    linkedRfiTagId: hasPhotoLink
                                      ? null
                                      : prevAnn.linkedRfiTagId ||
                                        ann.linkedRfiTagId,
                                    photoTag: prevAnn.photoTag
                                      ? {
                                          ...prevAnn.photoTag,
                                          connectionPoint: newConnectionCenter,
                                          tagPosition: newTagPosition,
                                        }
                                      : ann.photoTag,
                                    linkedPhotoUrl:
                                      prevAnn.linkedPhotoUrl ??
                                      ann.linkedPhotoUrl,
                                    linkedPhotoName:
                                      prevAnn.linkedPhotoName ??
                                      ann.linkedPhotoName,
                                    linkedPhotoPreviewUrl:
                                      prevAnn.linkedPhotoPreviewUrl ??
                                      ann.linkedPhotoPreviewUrl,
                                    linkedPhoto:
                                      prevAnn.linkedPhotoUrl ||
                                      ann.linkedPhotoUrl
                                        ? undefined
                                        : prevAnn.linkedPhotoPreviewUrl ||
                                            ann.linkedPhotoPreviewUrl
                                          ? undefined
                                          : (prevAnn.linkedPhoto ??
                                            ann.linkedPhoto),
                                    linkedPhotoTagId:
                                      prevAnn.linkedPhotoTagId ??
                                      ann.linkedPhotoTagId,
                                  }
                                : ann,
                            );
                          });

                          // Update rfiTags state with new connectionPoint and tagPosition
                          if (existingRfiTag) {
                            setRfiTags((prev) =>
                              prev.map((tag) =>
                                tag.id === existingRfiTag.id
                                  ? {
                                      ...tag,
                                      connectionPoint: newConnectionCenter,
                                      tagPosition: newTagPosition, // Update tag position so bottom edge aligns with box bottom
                                    }
                                  : tag,
                              ),
                            );
                          }

                          setSelectedAnnotationIndex(null);
                        } else {
                          // Create new connection annotation - save even if no text
                          const newConnection = addAnnotation({
                            type: "connection",
                            start: connectionBoxStart,
                            end: connectionBoxEnd,
                            content: "", // No textarea, just selection rectangle
                            page: pageIndex,
                          });
                          // Update RFI tags that were created for this connection and add rfiTag to annotation
                          let rfiTagToAdd = null;
                          setRfiTags((prev) =>
                            prev.map((tag) => {
                              if (!tag.connectionId && tag.page === pageIndex) {
                                const boxX = Math.min(
                                  connectionBoxStart.x,
                                  connectionBoxEnd.x,
                                );
                                const boxY = Math.min(
                                  connectionBoxStart.y,
                                  connectionBoxEnd.y,
                                );
                                const boxWidth = Math.abs(
                                  connectionBoxEnd.x - connectionBoxStart.x,
                                );
                                const boxHeight = Math.abs(
                                  connectionBoxEnd.y - connectionBoxStart.y,
                                );
                                const innerRectInset =
                                  CONNECTION_INNER_RECT_INSET; // Inner rectangle is inset by 8px from outer rectangle
                                const tagHeight = RFI_TAG_HEIGHT; // Tag height
                                const connectionCenter = {
                                  x: boxX + boxWidth - innerRectInset, // Inner right edge
                                  y: boxY + boxHeight - innerRectInset, // Inner bottom edge
                                };
                                // Tag position: tag should be positioned so its bottom edge aligns with inner rect bottom
                                const tagPosition = {
                                  x: connectionCenter.x,
                                  y:
                                    boxY +
                                    boxHeight -
                                    innerRectInset -
                                    tagHeight / 2, // Center tag vertically at inner rect bottom
                                };
                                const distance = Math.sqrt(
                                  Math.pow(
                                    tag.connectionPoint.x - connectionCenter.x,
                                    2,
                                  ) +
                                    Math.pow(
                                      tag.connectionPoint.y -
                                        connectionCenter.y,
                                      2,
                                    ),
                                );
                                if (distance < 50) {
                                  const updatedTag = {
                                    ...tag,
                                    connectionId: newConnection.id,
                                    connectionPoint: connectionCenter,
                                    tagPosition: tagPosition, // Update tag position so bottom edge aligns with box bottom
                                  };
                                  rfiTagToAdd = updatedTag;
                                  return updatedTag;
                                }
                              }
                              return tag;
                            }),
                          );

                          // Update annotation with rfiTag if found
                          if (rfiTagToAdd) {
                            setAnnotations((prevAnn) => {
                              const existingAnn = prevAnn.find(
                                (ann) => ann.id === newConnection.id,
                              );
                              if (existingAnn) {
                                return prevAnn.map((ann) =>
                                  ann.id === newConnection.id
                                    ? {
                                        ...ann,
                                        linkedRfiId: rfiTagToAdd.rfiId,
                                        linkedRfiTagId: rfiTagToAdd.id,
                                        rfiTag: rfiTagToAdd,
                                      }
                                    : ann,
                                );
                              }
                              return prevAnn;
                            });
                          }
                          if (pendingPhotoRef) {
                            const pf = buildPhotoLinkFields(
                              connectionBoxStart,
                              connectionBoxEnd,
                              pageIndex,
                              newConnection.id,
                              pendingPhotoRef,
                            );
                            setAnnotations((prevAnn) =>
                              prevAnn.map((ann) =>
                                ann.id === newConnection.id
                                  ? {
                                      ...ann,
                                      ...pf,
                                      linkedRfiId: null,
                                      linkedRfiTagId: null,
                                      rfiTag: null,
                                    }
                                  : ann,
                              ),
                            );
                            setRfiTags((prev) =>
                              prev.filter(
                                (t) => t.connectionId !== newConnection.id,
                              ),
                            );
                            if (pendingPhotoRef.file instanceof File) {
                              connectionPhotoFilesRef.current.set(
                                newConnection.id,
                                pendingPhotoRef.file,
                              );
                            }
                            setPendingPhotoRef(null);
                          }
                          // Clear selected annotation index for new connections
                          setSelectedAnnotationIndex(null);
                        }
                        // Clear connection box state after annotation is saved
                        setShowConnectionBox(false);
                        setConnectionBoxStart(null);
                        setConnectionBoxEnd(null);
                        setConnectionText("");
                        setCurrentTool(TOOLS.PAN); // Deactivate connection tool after saving
                        setPendingPhotoRef(null);
                      }
                    }
                  }
                  if (
                    cloudDrawingActive &&
                    currentTool === TOOLS.SHAPE &&
                    currentShape === SHAPES.CLOUD
                  ) {
                    if (!e.target.getAttribute("data-id")) {
                      if (isDrawing && startPoint) {
                        endDrawing(e);
                      }
                      if (startDrawing(e)) return;
                      return;
                    }
                  }
                  if (startDrawing(e)) return;
                }}
                onMouseMove={draw}
                onMouseUp={endDrawing}
                onMouseLeave={endDrawing}
                onTouchStart={handleSvgTouchStart}
                onTouchMove={handleSvgTouchMove}
                onTouchEnd={handleSvgTouchEnd}
              >
                <g transform={`scale(${zoom})`}>
                  {cloudDrawingActive && cloudStartPoint && (
                    <>
                      <circle
                        cx={cloudStartPoint.x}
                        cy={cloudStartPoint.y}
                        r={24}
                        fill="rgba(24, 144, 255, 0.1)"
                        stroke="#1890ff"
                        strokeWidth={1}
                        style={{ cursor: "pointer", pointerEvents: "all" }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          if (isDrawing && startPoint) {
                            finalizeCloudDrawing({
                              start: startPoint,
                              end: cloudStartPoint,
                            });
                          } else {
                            finalizeCloudDrawing();
                          }
                        }}
                      />
                      <circle
                        cx={cloudStartPoint.x}
                        cy={cloudStartPoint.y}
                        r={4}
                        fill="#1890ff"
                        opacity={0.4}
                        stroke="#1890ff"
                        strokeWidth={1.5}
                        style={{ cursor: "pointer", pointerEvents: "none" }}
                      />
                    </>
                  )}
                  <AnnotationRenderer
                    annotations={annotations}
                    pageIndex={pageIndex}
                    selectedAnnotationIndex={selectedAnnotationIndex}
                    editingTextAnnotationId={
                      showTextInput && selectedAnnotationIndex !== null
                        ? annotations[selectedAnnotationIndex]?.id
                        : null
                    }
                    showConnectionBox={showConnectionBox}
                    cloudDrawingActive={cloudDrawingActive}
                    currentTool={currentTool}
                    currentShape={currentShape}
                    isDrawing={isDrawing}
                    startPoint={startPoint}
                    rfiTags={rfiTags}
                    onAnnotationClick={handleAnnotationClick}
                    onAnnotationDoubleClick={handleAnnotationDoubleClick}
                    onCalloutKneeReset={(index) =>
                      setAnnotations((prev) =>
                        prev.map((ann, i) =>
                          i === index ? { ...ann, calloutKnee: undefined } : ann,
                        ),
                      )
                    }
                    onStartDrawing={startDrawing}
                    onEndDrawing={endDrawing}
                    visualZoom={visualZoom}
                    onTagClick={(tag) => {
                      const photoSrc =
                        tag?.type === "photo"
                          ? tag.displayUrl || tag.linkedPhoto
                          : null;
                      if (tag?.type === "photo" && photoSrc) {
                        setPhotoPreviewUrl(photoSrc);
                        return;
                      }
                      if (tag?.type === "drawing") {
                        goToLatestLinkedDrawing(tag);
                        return;
                      }
                      if (tag.rfiId) {
                        if (tag.type === "punchlist") {
                          setViewPunchListId(tag.punchListId);
                          setViewPunchListItemId(tag.rfiId);
                          setShowPunchListItemModal(true);
                        } else if (tag.type === "submittal") {
                          setSelectedRfiId(tag.rfiId);
                          setShowSubmittalModal(true);
                        } else {
                          setSelectedRfiId(tag.rfiId);
                          setShowRfiModal(true);
                        }
                      }
                    }}
                    onTagClose={(tag) => {
                      if (tag?.type === "drawing") {
                        const connectionId =
                          tag.connectionId ||
                          (selectedAnnotationIndex !== null
                            ? annotations[selectedAnnotationIndex]?.id
                            : null);
                        if (!connectionId) return;
                        setAnnotations((prev) =>
                          prev.map((ann) =>
                            ann.id === connectionId
                              ? {
                                  ...ann,
                                  targetDrawingId: undefined,
                                  targetDrawingNumber: undefined,
                                  targetFolderId: undefined,
                                  calloutKind: undefined,
                                  autoGenerated: undefined,
                                }
                              : ann,
                          ),
                        );
                        return;
                      }
                      if (tag?.type === "photo" && tag.connectionId) {
                        setAnnotations((prev) =>
                          prev.map((ann) => {
                            if (ann.id !== tag.connectionId) return ann;
                            if (
                              ann.linkedPhotoPreviewUrl?.startsWith("blob:")
                            ) {
                              try {
                                URL.revokeObjectURL(ann.linkedPhotoPreviewUrl);
                              } catch (e) {}
                            }
                            connectionPhotoFilesRef.current.delete(ann.id);
                            return {
                              ...ann,
                              linkedPhoto: null,
                              linkedPhotoUrl: null,
                              linkedPhotoName: null,
                              linkedPhotoPreviewUrl: null,
                              linkedPhotoTagId: null,
                              photoTag: null,
                            };
                          }),
                        );
                        return;
                      }
                      setRfiTags((prev) => prev.filter((t) => t.id !== tag.id));
                      // Remove link from connection
                      if (tag.connectionId) {
                        setAnnotations((prev) =>
                          prev.map((ann) =>
                            ann.id === tag.connectionId
                              ? {
                                  ...ann,
                                  linkedRfiId: null,
                                  linkedRfiTagId: null,
                                  rfiTag: null,
                                }
                              : ann,
                          ),
                        );
                      }
                    }}
                  />
                  {currentPath && (
                    <g>
                      <title>
                        {currentUser
                          ? `Added by: ${
                              `${currentUser.first_name || ""} ${
                                currentUser.last_name || ""
                              }`.trim() ||
                              currentUser.email ||
                              currentUser.username ||
                              "Unknown"
                            }`
                          : "Added by: Unknown"}
                      </title>
                      <path
                        d={currentPath}
                        stroke={color}
                        strokeWidth={strokeWidth}
                        fill="none"
                      />
                    </g>
                  )}
                  <CurrentShapeRenderer
                    isDrawing={isDrawing}
                    currentTool={currentTool}
                    currentShape={currentShape}
                    startPoint={startPoint}
                    currentPath={currentPath}
                    color={color}
                    strokeWidth={strokeWidth}
                    currentUser={currentUser}
                  />
                  {/* Show connection box while drawing */}
                  {currentTool === TOOLS.CONNECTION &&
                    startPoint &&
                    connectionBoxEnd &&
                    !showConnectionBox && (
                      <rect
                        x={Math.min(startPoint.x, connectionBoxEnd.x)}
                        y={Math.min(startPoint.y, connectionBoxEnd.y)}
                        width={Math.abs(connectionBoxEnd.x - startPoint.x)}
                        height={Math.abs(connectionBoxEnd.y - startPoint.y)}
                        fill="rgba(24, 144, 255, 0.1)"
                        stroke="#1890ff"
                        strokeWidth={2}
                        strokeDasharray="5,5"
                      />
                    )}
                  {showConnectionBox &&
                    connectionBoxStart &&
                    connectionBoxEnd && (
                      <ConnectionBox
                        connectionBoxStart={connectionBoxStart}
                        connectionBoxEnd={connectionBoxEnd}
                        zoom={zoom}
                        canvasRef={canvasRef}
                        onMoveStart={handleConnectionMoveStart}
                        onResizeStart={handleConnectionResizeStart}
                        onPublish={handleConnectionPublish}
                        onLink={handleConnectionLink}
                        onDelete={handleConnectionDelete}
                        linkedDrawing={
                          selectedAnnotationIndex !== null &&
                          isDrawingLink(annotations[selectedAnnotationIndex])
                            ? annotations[selectedAnnotationIndex]
                            : null
                        }
                        canDelete={canDeleteConnectionBox}
                        linkedRfi={(() => {
                          // If editing an existing connection, find tag by connectionId
                          if (
                            selectedAnnotationIndex !== null &&
                            annotations[selectedAnnotationIndex]?.type ===
                              "connection"
                          ) {
                            const connectionId =
                              annotations[selectedAnnotationIndex].id;
                            return rfiTags.find(
                              (tag) =>
                                tag.connectionId === connectionId &&
                                tag.page === pageIndex,
                            );
                          }
                          // Otherwise find unsaved tag (new connection)
                          return rfiTags.find(
                            (tag) =>
                              !tag.connectionId && tag.page === pageIndex,
                          );
                        })()}
                        linkedPhotoSrc={
                          (pendingPhotoRef &&
                            (pendingPhotoRef.objectUrl ||
                              pendingPhotoRef.url ||
                              pendingPhotoRef.linkedPhoto)) ||
                          (selectedAnnotationIndex !== null &&
                            annotations[selectedAnnotationIndex]?.type ===
                              "connection" &&
                            getLinkedPhotoDisplayUrl(
                              annotations[selectedAnnotationIndex],
                            )) ||
                          null
                        }
                        onPhotoTagClick={(url) => setPhotoPreviewUrl(url)}
                        onPhotoUnlink={() => {
                          if (pendingPhotoRef) {
                            if (
                              pendingPhotoRef.objectUrl?.startsWith("blob:")
                            ) {
                              try {
                                URL.revokeObjectURL(pendingPhotoRef.objectUrl);
                              } catch (e) {}
                            }
                            setPendingPhotoRef(null);
                            return;
                          }
                          if (
                            selectedAnnotationIndex !== null &&
                            annotations[selectedAnnotationIndex]?.type ===
                              "connection"
                          ) {
                            const ann = annotations[selectedAnnotationIndex];
                            const cid = ann.id;
                            if (
                              ann.linkedPhotoPreviewUrl?.startsWith("blob:")
                            ) {
                              try {
                                URL.revokeObjectURL(ann.linkedPhotoPreviewUrl);
                              } catch (e) {}
                            }
                            connectionPhotoFilesRef.current.delete(cid);
                            setAnnotations((prev) =>
                              prev.map((a) =>
                                a.id === cid
                                  ? {
                                      ...a,
                                      linkedPhoto: null,
                                      linkedPhotoUrl: null,
                                      linkedPhotoName: null,
                                      linkedPhotoPreviewUrl: null,
                                      linkedPhotoTagId: null,
                                      photoTag: null,
                                    }
                                  : a,
                              ),
                            );
                          }
                        }}
                        onTagClick={(tag) => {
                          if (tag?.type === "drawing") {
                            goToLatestLinkedDrawing(tag);
                            return;
                          }
                          if (tag.rfiId) {
                            if (tag.type === "punchlist") {
                              setViewPunchListId(tag.punchListId);
                              setViewPunchListItemId(tag.rfiId);
                              setShowPunchListItemModal(true);
                            } else if (tag.type === "submittal") {
                              setSelectedRfiId(tag.rfiId);
                              setShowSubmittalModal(true);
                            } else {
                              setSelectedRfiId(tag.rfiId);
                              setShowRfiModal(true);
                            }
                          }
                        }}
                        onTagClose={(tag) => {
                          if (tag?.type === "drawing") {
                            const connectionId =
                              tag.connectionId ||
                              (selectedAnnotationIndex !== null
                                ? annotations[selectedAnnotationIndex]?.id
                                : null);
                            if (!connectionId) return;
                            setAnnotations((prev) =>
                              prev.map((ann) =>
                                ann.id === connectionId
                                  ? {
                                      ...ann,
                                      targetDrawingId: undefined,
                                      targetDrawingNumber: undefined,
                                      targetFolderId: undefined,
                                      calloutKind: undefined,
                                      autoGenerated: undefined,
                                    }
                                  : ann,
                              ),
                            );
                            return;
                          }
                          setRfiTags((prev) =>
                            prev.filter((t) => t.id !== tag.id),
                          );
                          // Remove link from connection
                          if (tag.connectionId) {
                            setAnnotations((prev) =>
                              prev.map((ann) =>
                                ann.id === tag.connectionId
                                  ? {
                                      ...ann,
                                      linkedRfiId: null,
                                      linkedRfiTagId: null,
                                      rfiTag: null,
                                    }
                                  : ann,
                              ),
                            );
                          }
                        }}
                      />
                    )}
                  {currentTool === TOOLS.CALLOUT &&
                    isDrawing &&
                    startPoint &&
                    calloutDragEnd &&
                    (() => {
                      const center = getCalloutBoxCenter(
                        startPoint,
                        calloutDragEnd,
                        TEXT_ANNOTATION_DEFAULT_WIDTH,
                      );
                      return (
                        <CalloutLeader
                          tip={startPoint}
                          box={{
                            left: center.x - TEXT_ANNOTATION_DEFAULT_WIDTH / 2,
                            top: center.y - 12,
                            width: TEXT_ANNOTATION_DEFAULT_WIDTH,
                            height: 24,
                          }}
                          color={color}
                          dashed
                        />
                      );
                    })()}
                  {showTextInput && textInputPos && calloutTip && (
                    <CalloutLeader
                      tip={calloutTip}
                      knee={calloutKnee}
                      box={getEditorCalloutBox()}
                      color={color}
                      showHandles
                      tipHitProps={{
                        style: { cursor: "crosshair" },
                        onPointerDown: handleEditorCalloutHandlePointerDown("tip"),
                        onTouchStart: (e) => e.stopPropagation(),
                      }}
                      kneeHitProps={{
                        style: { cursor: "crosshair" },
                        onPointerDown: handleEditorCalloutHandlePointerDown("knee"),
                        onTouchStart: (e) => e.stopPropagation(),
                        onDoubleClick: () => setEditorCalloutPoint("knee", null),
                      }}
                    />
                  )}
                  {showTextInput && textInputPos && (
                    <TextAnnotationInput
                      textInput={textInput}
                      textInputPos={textInputPos}
                      inputWidth={inputWidth}
                      inputHeight={inputHeight}
                      fontSize={fontSize}
                      color={color}
                      textBold={textBold}
                      textItalic={textItalic}
                      textUnderline={textUnderline}
                      textStrike={textStrike}
                      inputRef={inputRef}
                      zoom={zoom}
                      onBoxResize={({ width, height, position }) => {
                        setInputWidth(width);
                        setInputHeight(height);
                        manualTextHeightRef.current = height;
                        if (position) {
                          setTextInputPos(position);
                          textInputPosRef.current = position;
                        }
                        if (selectedAnnotationIndex !== null) {
                          setAnnotations((prev) =>
                            prev.map((ann, i) =>
                              i === selectedAnnotationIndex
                                ? {
                                    ...ann,
                                    boxWidth: width,
                                    ...(position ? { position } : {}),
                                  }
                                : ann,
                            ),
                          );
                        }
                      }}
                      onBoxMove={({ x, y }) => {
                        const next = { x, y };
                        const prevPos = textInputPosRef.current || textInputPos;
                        const knee = calloutKneeRef.current;
                        const movedKnee =
                          knee && prevPos
                            ? {
                                x: knee.x + next.x - prevPos.x,
                                y: knee.y + next.y - prevPos.y,
                              }
                            : knee;
                        calloutKneeRef.current = movedKnee;
                        setCalloutKnee(movedKnee);
                        setTextInputPos(next);
                        textInputPosRef.current = next;
                        if (selectedAnnotationIndex !== null) {
                          setAnnotations((prev) =>
                            prev.map((ann, i) =>
                              i === selectedAnnotationIndex
                                ? {
                                    ...ann,
                                    position: next,
                                    ...(movedKnee ? { calloutKnee: movedKnee } : {}),
                                  }
                                : ann,
                            ),
                          );
                        }
                      }}
                      onResizeStart={() => {
                        isResizingTextRef.current = true;
                      }}
                      onResizeEnd={() => {
                        isResizingTextRef.current = false;
                        inputRef.current?.focus?.();
                      }}
                      onMoveStart={() => {
                        isResizingTextRef.current = true;
                      }}
                      onMoveEnd={() => {
                        isResizingTextRef.current = false;
                        inputRef.current?.focus?.();
                      }}
                      onTextChange={(e) => {
                        setTextInput(e.target.value);
                        textInputRef.current = e.target.value;
                        if (selectedAnnotationIndex !== null) {
                          setAnnotations((prev) =>
                            prev.map((ann, i) =>
                              i === selectedAnnotationIndex
                                ? {
                                    ...ann,
                                    content: e.target.value,
                                    boxWidth: inputWidth,
                                    style: {
                                      fontWeight: textBold ? "bold" : "normal",
                                      fontStyle: textItalic
                                        ? "italic"
                                        : "normal",
                                      textDecoration: `${
                                        textUnderline ? "underline " : ""
                                      }${textStrike ? "line-through" : ""}`.trim(),
                                      highlight: textHighlight,
                                      squiggly: textSquiggly,
                                    },
                                    fontSize,
                                    color,
                                  }
                                : ann,
                            ),
                          );
                        }
                      }}
                      onBlur={(e) => {
                        if (isResizingTextRef.current || suppressTextBlurRef.current) {
                          return;
                        }
                        // Keep editor open when focus moves to the drawing toolbar
                        // (font size / color / style controls).
                        const next = e.relatedTarget;
                        if (
                          next &&
                          typeof next.closest === "function" &&
                          next.closest("[data-drawing-toolbar]")
                        ) {
                          return;
                        }
                        // Ant Slider / some controls don't set relatedTarget — defer and
                        // cancel if focus returned to the textarea or landed in the toolbar.
                        setTimeout(() => {
                          if (
                            isResizingTextRef.current ||
                            suppressTextBlurRef.current
                          ) {
                            return;
                          }
                          if (document.activeElement === inputRef.current) return;
                          if (
                            document.activeElement?.closest?.(
                              "[data-drawing-toolbar]",
                            )
                          ) {
                            return;
                          }
                          addTextAnnotation(textInputRef.current || textInput);
                        }, 0);
                      }}
                      onKeyDown={(e) => {
                        // Keep typing keys from triggering drawing shortcuts
                        // (e.g. Delete). Enter inserts a newline; Escape commits.
                        e.stopPropagation();
                        if (e.key === "Escape") {
                          e.preventDefault();
                          addTextAnnotation(textInputRef.current || textInput);
                        }
                      }}
                    />
                  )}
                </g>
                {/* Arrow marker definition */}
                <defs>
                  <marker
                    id="arrowhead-red"
                    markerWidth="10"
                    markerHeight="10"
                    refX="9"
                    refY="3"
                    orient="auto"
                  >
                    <polygon points="0 0, 10 3, 0 6" fill="#ff4d4f" />
                  </marker>
                </defs>
                {/* Search result highlights */}
                {searchResults
                  .filter((result) => result.page === pageIndex)
                  .map((result, index) => {
                    const globalIndex = searchResults.findIndex(
                      (r) => r === result,
                    );
                    const isCurrentMatch = globalIndex === currentMatchIndex;
                    return (
                      <rect
                        key={`search-${globalIndex}`}
                        x={result.x * zoom}
                        y={(result.y - result.height) * zoom}
                        width={Math.max(result.width || 50, 20) * zoom}
                        height={(result.height || 15) * zoom}
                        fill={
                          isCurrentMatch
                            ? "rgba(255, 255, 0, 0.6)"
                            : "rgba(255, 255, 0, 0.4)"
                        }
                        style={{ pointerEvents: "none" }}
                      />
                    );
                  })}
              </svg>
              <SheetLinkPopover
                annotation={sheetLinkMenuAnnotation}
                anchor={sheetLinkMenuAnchor}
                canDelete={canDeleteAnnotationAt(sheetLinkMenuIndex)}
                onClose={() => setSheetLinkMenuId(null)}
                onGoTo={handleSheetLinkGoTo}
                onChangeTarget={handleChangeSheetLinkTarget}
                onDelete={handleDeleteSheetLink}
              />
            </div>
          </div>
          </div>
        </div>
      </DrawingPreviewShell>
      {showRfiModal && (
        <ViewRFIModal
          visible={showRfiModal}
          onClose={() => {
            setShowRfiModal(false);
            setSelectedRfiId(null);
          }}
          rfiId={selectedRfiId}
        />
      )}
      {showSubmittalModal && (
        <ViewSubmittalModal
          visible={showSubmittalModal}
          onClose={() => {
            setShowSubmittalModal(false);
            setSelectedRfiId(null);
          }}
          submittalId={selectedRfiId}
        />
      )}
      {showPunchListItemModal && viewPunchListId && viewPunchListItemId && (
        <ViewPunchListItemModal
          visible={showPunchListItemModal}
          onClose={() => {
            setShowPunchListItemModal(false);
            setViewPunchListId(null);
            setViewPunchListItemId(null);
          }}
          punchListId={viewPunchListId}
          punchListItemId={viewPunchListItemId}
        />
      )}
    </div>
  );
};

export default DrawingPreview;
